import {
  ArrowRight,
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronDown,
  Clock3,
  ClipboardCheck,
  CreditCard,
  Database,
  FileCheck2,
  Filter,
  HeartHandshake,
  LayoutDashboard,
  LineChart,
  ListChecks,
  LockKeyhole,
  MessageSquare,
  PackageCheck,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Store,
  UploadCloud,
  UserRound,
  UserRoundCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { useMemo, useState } from "react";
import portraitSheet from "./assets/therapist-portraits.png";
import {
  defaultAdminState,
  defaultMarketplaceState,
  defaultPatientProfile,
  defaultRegistrationState,
  defaultSpecialistProfile,
  demoSchedule,
  scheduleDays,
  type AdminBootstrapProfile,
  type AdminApplication,
  type AdminBooking,
  type AdminCase,
  type AdminState,
  type ClientRegistration,
  type ClientMarketplaceRequest,
  type PatientProfile,
  type MarketplaceState,
  type PricingModel,
  type RegistrationRole,
  type RegistrationState,
  type SpecialistMarketplaceApplication,
  type SpecialistRegistration,
  type SpecialistProfile,
  type Workspace,
} from "./data/prototype";
import {
  budgetOptions,
  formatOptions,
  requestOptions,
  therapists,
  timeOptions,
  type Therapist,
} from "./data/therapists";
import { useLocalStorageState } from "./hooks/useLocalStorageState";

type Answers = {
  requests: string[];
  budget: string;
  format: "Видео" | "Чат" | "Аудио";
  time: string;
};

const initialAnswers: Answers = {
  requests: ["Мне тревожно", "Выгорание"],
  budget: "5-8 тыс. ₽",
  format: "Видео",
  time: "Сегодня вечером",
};

type MarketplaceService = {
  title: string;
  audience: "Клиент" | "Специалист" | "Администратор";
  price: string;
  status: string;
  text: string;
};

type MarketplaceClient = {
  name: string;
  request: string;
  budget: string;
  status: string;
  route: string;
};

type MarketplaceFlowStep = {
  title: string;
  text: string;
  owner: string;
};

type MarketplaceMode = "home" | "client-signup" | "specialist-signup";

type ClientSignupDraft = {
  selectedService: string;
  selectedSpecialist: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  telegram: string;
  vkId: string;
  confirmationChannel: string;
  verificationSent: boolean;
  verificationCode: string;
  contactConfirmed: boolean;
  mainComplaint: string;
  therapyGoals: string;
  symptoms: string[];
  formatPreference: string;
  budget: string;
  scheduleWindows: string;
  acceptedProcessing: boolean;
};

type SpecialistSignupDraft = {
  serviceTitle: string;
  serviceDescription: string;
  publicName: string;
  legalName: string;
  email: string;
  phone: string;
  telegram: string;
  vkId: string;
  confirmationChannel: string;
  verificationSent: boolean;
  verificationCode: string;
  contactConfirmed: boolean;
  professionalRole: string;
  city: string;
  education: string;
  experienceYears: string;
  formats: string[];
  approaches: string[];
  introSession: number;
  regularSession: number;
  portfolioLinks: string;
  acceptedVerification: boolean;
};

const demoVerificationCode = "246810";
const contactChannelOptions = ["E-mail", "Телефон", "Telegram", "VK ID"];

const initialClientSignupDraft: ClientSignupDraft = {
  selectedService: "Первичная консультация",
  selectedSpecialist: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  email: "",
  phone: "",
  telegram: "",
  vkId: "",
  confirmationChannel: "E-mail",
  verificationSent: false,
  verificationCode: "",
  contactConfirmed: false,
  mainComplaint: "",
  therapyGoals: "",
  symptoms: [],
  formatPreference: "Видео",
  budget: "5-8 тыс. ₽",
  scheduleWindows: "",
  acceptedProcessing: false,
};

const initialSpecialistSignupDraft: SpecialistSignupDraft = {
  serviceTitle: "Индивидуальные консультации",
  serviceDescription: "",
  publicName: "",
  legalName: "",
  email: "",
  phone: "",
  telegram: "",
  vkId: "",
  confirmationChannel: "E-mail",
  verificationSent: false,
  verificationCode: "",
  contactConfirmed: false,
  professionalRole: "",
  city: "",
  education: "",
  experienceYears: "",
  formats: ["Видео"],
  approaches: [],
  introSession: 0,
  regularSession: 0,
  portfolioLinks: "",
  acceptedVerification: false,
};

const specialistServiceTemplates = [
  "Индивидуальные консультации",
  "Первичная диагностическая встреча",
  "Пакет сопровождения",
  "Групповая работа",
  "Супервизия",
];

const marketplaceServices: MarketplaceService[] = [
  {
    title: "Первичная консультация",
    audience: "Клиент",
    price: "от 3 500 ₽",
    status: "Каталог",
    text: "Отдельный первый приём, чтобы проверить контакт и уточнить запрос без длинного обязательства.",
  },
  {
    title: "Регулярная терапия",
    audience: "Клиент",
    price: "по тарифу специалиста",
    status: "Бронирование",
    text: "Стандартная сессия 50 минут с расписанием, напоминаниями и возможностью переноса.",
  },
  {
    title: "Пакет 4/8 сессий",
    audience: "Клиент",
    price: "гибкая скидка",
    status: "Заглушка",
    text: "Будущий продуктовый блок для курсовой работы, корпоративных клиентов и предоплат.",
  },
  {
    title: "Супервизия и интервизия",
    audience: "Специалист",
    price: "по правилам платформы",
    status: "Позже",
    text: "Внутренний сервис для качества практики, проверки сложных случаев и поддержки специалистов.",
  },
];

const marketplaceClients: MarketplaceClient[] = [
  {
    name: "Мария Г.",
    request: "Тревога, выгорание, нужен бережный старт",
    budget: "5-8 тыс. ₽",
    status: "Анкета сохранена",
    route: "Подбор",
  },
  {
    name: "Алексей П.",
    request: "Прокрастинация, стресс, работа с привычками",
    budget: "До 3 тыс. ₽",
    status: "Ожидает запись",
    route: "Каталог",
  },
  {
    name: "Елена С.",
    request: "Отношения, запрос на замену специалиста",
    budget: "8-12 тыс. ₽",
    status: "Поддержка",
    route: "Замена",
  },
];

const clientFlowSteps: MarketplaceFlowStep[] = [
  {
    title: "Вход и анкета",
    text: "Клиент вводит личные данные, жалобы, цели, формат, бюджет и согласия.",
    owner: "Клиент",
  },
  {
    title: "Подбор и каталог",
    text: "Система показывает подходящих специалистов, фильтры, цены, слоты и ограничения.",
    owner: "Marketplace",
  },
  {
    title: "Запись и кабинет",
    text: "Клиент видит ближайшую сессию, трекер состояния, сообщения и запрос на замену.",
    owner: "Клиент",
  },
];

const specialistFlowSteps: MarketplaceFlowStep[] = [
  {
    title: "Заявка специалиста",
    text: "Личные данные, квалификация, документы, опыт, подходы, границы и публичная карточка.",
    owner: "Специалист",
  },
  {
    title: "Модерация",
    text: "Администратор проверяет документы, интервью, профиль, тарифы и допуск в каталог.",
    owner: "Администратор",
  },
  {
    title: "Рабочий кабинет",
    text: "Специалист управляет профилем, тарифами, расписанием и форматами, не видя базу клиентов.",
    owner: "Специалист",
  },
];

const operationalAreas = [
  {
    title: "Модерация специалистов",
    text: "Заявки, документы, интервью, допуск к публикации.",
    status: "3 заявки",
  },
  {
    title: "Бронирования",
    text: "Слоты, переносы, подтверждения, отмены и напоминания.",
    status: "3 записи",
  },
  {
    title: "Поддержка и замены",
    text: "Жалобы, запросы на замену, возвраты, контроль коммуникации.",
    status: "3 кейса",
  },
  {
    title: "Платежи",
    text: "Комиссия, чеки, выплаты, фискализация и юридическая модель.",
    status: "Заглушка",
  },
];

const integrationAreas = [
  "Авторизация и роли",
  "Хранилище документов",
  "Платежи и чеки",
  "Уведомления",
  "Журнал действий",
  "Аналитика качества",
];

const levelLabel: Record<Therapist["level"], string> = {
  junior: "junior",
  middle: "middle",
  senior: "senior",
  expert: "expert",
};

const registrationRoleTabs: Array<{ id: RegistrationRole; label: string }> = [
  { id: "client", label: "Клиент" },
  { id: "specialist", label: "Специалист" },
  { id: "admin", label: "Администратор" },
];

const rolePortalCopy: Record<
  RegistrationRole,
  { title: string; text: string; progressLabel: string; nextArea: string }
> = {
  client: {
    title: "Анкета клиента",
    text: "Заполните личные данные, жалобы и предпочтения. После анкеты клиент попадает в подбор и свой кабинет.",
    progressLabel: "Заполнение клиентской анкеты",
    nextArea: "Дальше: подбор специалиста и кабинет пациента",
  },
  specialist: {
    title: "Заявка специалиста",
    text: "Заполните данные, квалификацию, документы, опыт, подходы и ссылки. Клиентские анкеты специалисту не показываются.",
    progressLabel: "Заполнение заявки специалиста",
    nextArea: "Дальше: модерация и кабинет специалиста",
  },
  admin: {
    title: "Создание администратора",
    text: "Администратор создаётся отдельно, получает уровень доступа и дальше координирует заявки, клиентов и специалистов.",
    progressLabel: "Заполнение административного доступа",
    nextArea: "Дальше: админ-панель и очереди модерации",
  },
};

const roleInitials: Record<RegistrationRole, string> = {
  client: "КЛ",
  specialist: "СП",
  admin: "АД",
};

function getRoleWorkspaceTabs(role: RegistrationRole | ""): Array<{ id: Workspace; label: string }> {
  if (!role) {
    return [
      { id: "marketplace", label: "Маркетплейс" },
      { id: "registration", label: "Вход" },
    ];
  }

  if (role === "client") {
    return [
      { id: "marketplace", label: "Маркетплейс" },
      { id: "registration", label: "Анкета" },
      { id: "matching", label: "Подбор" },
      { id: "patient", label: "Мой кабинет" },
    ];
  }

  if (role === "specialist") {
    return [
      { id: "marketplace", label: "Маркетплейс" },
      { id: "registration", label: "Заявка" },
      { id: "specialist", label: "Кабинет" },
    ];
  }

  return [
    { id: "marketplace", label: "Маркетплейс" },
    { id: "registration", label: "Доступ" },
    { id: "admin", label: "Админ-панель" },
  ];
}

const specialistApproachOptions = ["КПТ", "ACT", "EMDR", "Схема-терапия", "CFT", "Гештальт"];
const specialistFormatOptions = ["Видео", "Аудио", "Чат", "Очно"];
const clientSymptomOptions = [
  "Тревога",
  "Выгорание",
  "Проблемы со сном",
  "Панические атаки",
  "Сложности в отношениях",
  "Потеря мотивации",
];

type CompletionValue = string | number | boolean | string[] | undefined;

function getCompletion(values: CompletionValue[]) {
  const completed = values.filter((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value > 0;
    }
    return Boolean(value?.trim());
  }).length;

  return {
    completed,
    total: values.length,
    percent: Math.round((completed / values.length) * 100),
  };
}

function scoreTherapist(therapist: Therapist, answers: Answers) {
  const topicScore = answers.requests.reduce(
    (score, item) => score + (therapist.topics.includes(item) ? 8 : 0),
    0,
  );
  const formatScore = therapist.formats.includes(answers.format) ? 5 : 0;
  const timeScore = therapist.timeTags.includes(answers.time) ? 4 : 0;
  const budgetScore =
    answers.budget === "До 3 тыс. ₽"
      ? therapist.price <= 3500
        ? 5
        : 0
      : answers.budget === "5-8 тыс. ₽"
        ? therapist.price >= 4500 && therapist.price <= 8000
          ? 5
          : 1
        : answers.budget === "8-12 тыс. ₽"
          ? therapist.price >= 8000 && therapist.price <= 12000
            ? 5
            : 1
          : therapist.price >= 9000
            ? 5
            : 0;

  return topicScore + formatScore + timeScore + budgetScore + therapist.rating;
}

function formatRub(price: number) {
  return new Intl.NumberFormat("ru-RU").format(price);
}

function formatToday() {
  return new Intl.DateTimeFormat("ru-RU").format(new Date());
}

function createRecordId(prefix: "CL" | "SP") {
  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const randomPart = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${datePart}-${randomPart}`;
}

function hasClientContact(draft: ClientSignupDraft) {
  return Boolean(draft.email.trim() || draft.phone.trim() || draft.telegram.trim() || draft.vkId.trim());
}

function hasSpecialistContact(draft: SpecialistSignupDraft) {
  return Boolean(draft.email.trim() || draft.phone.trim() || draft.telegram.trim() || draft.vkId.trim());
}

function App() {
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace>("marketplace");
  const [answers, setAnswers] = useState<Answers>(initialAnswers);
  const [activeTherapistId, setActiveTherapistId] = useState(therapists[0].id);
  const [selectedSlot, setSelectedSlot] = useState(therapists[0].slots[0]);
  const [bookingState, setBookingState] = useState<"idle" | "saved">("idle");
  const [catalogFilter, setCatalogFilter] = useState("Все подходы");
  const [registrationState, setRegistrationState] = useLocalStorageState(
    "mindplace.registration-state.v1",
    defaultRegistrationState,
  );
  const [patientProfile, setPatientProfile] = useLocalStorageState(
    "mindplace.patient-profile.v1",
    defaultPatientProfile,
  );
  const [specialistProfile, setSpecialistProfile] = useLocalStorageState(
    "mindplace.specialist-profile.v1",
    defaultSpecialistProfile,
  );
  const [adminState, setAdminState] = useLocalStorageState(
    "mindplace.admin-state.v1",
    defaultAdminState,
  );
  const [marketplaceState, setMarketplaceState] = useLocalStorageState(
    "mindplace.marketplace-state.v1",
    defaultMarketplaceState,
  );
  const visibleWorkspaceTabs = getRoleWorkspaceTabs(registrationState.activeRole);
  const activeVisibleWorkspace = visibleWorkspaceTabs.some((tab) => tab.id === activeWorkspace)
    ? activeWorkspace
    : "marketplace";

  const rankedTherapists = useMemo(() => {
    return therapists
      .map((therapist) => ({ therapist, score: scoreTherapist(therapist, answers) }))
      .sort((a, b) => b.score - a.score)
      .map(({ therapist }) => therapist);
  }, [answers]);

  const topMatches = rankedTherapists.slice(0, 3);
  const activeTherapist =
    therapists.find((therapist) => therapist.id === activeTherapistId) ?? topMatches[0];

  function toggleRequest(request: string) {
    setBookingState("idle");
    setAnswers((current) => {
      const exists = current.requests.includes(request);
      const requests = exists
        ? current.requests.filter((item) => item !== request)
        : [...current.requests, request].slice(-3);
      return { ...current, requests: requests.length ? requests : [request] };
    });
  }

  function chooseTherapist(therapist: Therapist) {
    setActiveTherapistId(therapist.id);
    setSelectedSlot(therapist.slots[0]);
    setBookingState("idle");
  }

  function openRole(role: RegistrationRole, workspace: Workspace = "registration") {
    setRegistrationState((current) => ({ ...current, activeRole: role }));
    setActiveWorkspace(workspace);
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="brand brand-button"
          onClick={() => setActiveWorkspace("marketplace")}
          type="button"
          aria-label="MindPlace home"
        >
          <span className="brand-mark">
            <HeartHandshake size={22} aria-hidden="true" />
          </span>
          <span>MindPlace</span>
        </button>
        <nav className="nav-links" aria-label="Основная навигация">
          {visibleWorkspaceTabs.map((tab) => (
            <button
              key={tab.id}
              className={activeVisibleWorkspace === tab.id ? "active" : ""}
              onClick={() => setActiveWorkspace(tab.id)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <label className="search-box">
          <Search size={18} aria-hidden="true" />
          <input placeholder="Поиск по системе" aria-label="Поиск по системе" />
        </label>
        <div className="topbar-actions">
          <button className="icon-button" aria-label="Сообщения">
            <MessageSquare size={18} />
          </button>
          <button className="icon-button" aria-label="Уведомления">
            <Bell size={18} />
          </button>
          <button className="profile-button" aria-label="Профиль прототипа">
            {registrationState.activeRole ? roleInitials[registrationState.activeRole] : "ВХ"}
            <ChevronDown size={16} />
          </button>
        </div>
      </header>

      <main>
        <div className="prototype-status">
          <LockKeyhole size={16} />
          Без реальных ПД — локальное демо. Все изменения сохраняются только в браузере.
        </div>

        {activeVisibleWorkspace === "marketplace" ? (
          <MarketplaceOverview
            adminState={adminState}
            catalogFilter={catalogFilter}
            marketplaceState={marketplaceState}
            onChooseTherapist={chooseTherapist}
            onOpenRole={openRole}
            rankedTherapists={rankedTherapists}
            selectedTherapistId={activeTherapistId}
            setAdminState={setAdminState}
            setCatalogFilter={setCatalogFilter}
            setMarketplaceState={setMarketplaceState}
          />
        ) : activeVisibleWorkspace === "registration" ? (
          <RegistrationWorkspace state={registrationState} setState={setRegistrationState} />
        ) : activeVisibleWorkspace === "matching" ? (
          <>
        <section id="matching" className="workspace" aria-label="Подбор специалиста">
          <aside className="panel questionnaire">
            <div className="panel-heading">
              <div>
                <h1>Подобрать специалиста</h1>
                <p>Шаг 3 из 6</p>
              </div>
              <span className="time-pill">~2 мин</span>
            </div>
            <div className="progress-row" aria-hidden="true">
              <span className="done" />
              <span className="done" />
              <span className="done" />
              <span />
              <span />
            </div>

            <QuestionBlock title="С чем вы хотите поработать?" hint="Можно выбрать несколько вариантов">
              <div className="chip-grid">
                {requestOptions.map((option) => (
                  <button
                    key={option}
                    className={answers.requests.includes(option) ? "chip selected" : "chip"}
                    onClick={() => toggleRequest(option)}
                    type="button"
                  >
                    {answers.requests.includes(option) && <Check size={15} />}
                    {option}
                  </button>
                ))}
              </div>
            </QuestionBlock>

            <QuestionBlock title="Какой бюджет комфортен за сессию?">
              <div className="chip-grid two">
                {budgetOptions.map((option) => (
                  <button
                    key={option}
                    className={answers.budget === option ? "chip selected" : "chip"}
                    onClick={() => {
                      setBookingState("idle");
                      setAnswers((current) => ({ ...current, budget: option }));
                    }}
                    type="button"
                  >
                    {answers.budget === option && <Check size={15} />}
                    {option}
                  </button>
                ))}
              </div>
            </QuestionBlock>

            <QuestionBlock title="Какой формат вам удобнее?">
              <div className="chip-grid two">
                {formatOptions.map((option) => (
                  <button
                    key={option}
                    className={answers.format === option ? "chip selected" : "chip"}
                    onClick={() => {
                      setBookingState("idle");
                      setAnswers((current) => ({ ...current, format: option }));
                    }}
                    type="button"
                  >
                    {answers.format === option && <Check size={15} />}
                    {option}
                  </button>
                ))}
              </div>
            </QuestionBlock>

            <QuestionBlock title="Когда вам удобно?">
              <div className="chip-grid compact">
                {timeOptions.map((option) => (
                  <button
                    key={option}
                    className={answers.time === option ? "chip selected" : "chip"}
                    onClick={() => {
                      setBookingState("idle");
                      setAnswers((current) => ({ ...current, time: option }));
                    }}
                    type="button"
                  >
                    {answers.time === option && <Check size={15} />}
                    {option}
                  </button>
                ))}
              </div>
            </QuestionBlock>

            <button className="primary-action" type="button">
              Показать подбор ({rankedTherapists.length})
            </button>
            <button
              className="text-action"
              onClick={() => {
                setAnswers(initialAnswers);
                setActiveTherapistId(therapists[0].id);
                setSelectedSlot(therapists[0].slots[0]);
                setBookingState("idle");
              }}
              type="button"
            >
              Сбросить ответы
            </button>
            <p className="privacy-note">
              <LockKeyhole size={15} />
              Это прототип. Ответы сохраняются только в вашем браузере.
            </p>
          </aside>

          <section className="results-area">
            <div className="results-toolbar">
              <div>
                <h2>Мы подобрали {rankedTherapists.length} специалистов</h2>
                <p>На основе ваших ответов и доступных слотов</p>
              </div>
              <button className="secondary-action" type="button">
                <Sparkles size={17} />
                Сохранить подбор
              </button>
            </div>

            <div className="match-list">
              {topMatches.map((therapist, index) => (
                <TherapistCard
                  key={therapist.id}
                  therapist={therapist}
                  active={therapist.id === activeTherapistId}
                  best={index === 0}
                  onSelect={() => chooseTherapist(therapist)}
                />
              ))}
            </div>

            <button className="load-more" type="button">
              Показать ещё {Math.max(rankedTherapists.length - 3, 0)} специалиста
              <ChevronDown size={16} />
            </button>
          </section>

          <aside className="panel booking">
            <div className="panel-heading">
              <div>
                <h2>Бронирование</h2>
                <p>Черновик записи</p>
              </div>
              <ChevronDown size={18} />
            </div>

            <div className="mini-profile">
              <Portrait index={activeTherapist.portraitIndex} />
              <div>
                <strong>{activeTherapist.name}</strong>
                <span>{activeTherapist.title}</span>
                <b>{formatRub(activeTherapist.price)} ₽ / сессия</b>
              </div>
            </div>

            <div className="booking-block">
              <h3>Выбранное время</h3>
              <div className="date-strip" aria-label="Выбор дня">
                {["Сегодня 19", "Сб 20", "Вс 21", "Пн 22", "Вт 23"].map((day, index) => (
                  <button key={day} className={index === 0 ? "date active" : "date"} type="button">
                    {day}
                  </button>
                ))}
              </div>
              <div className="slot-grid">
                {activeTherapist.slots.map((slot) => (
                  <button
                    key={slot}
                    className={selectedSlot === slot ? "slot active" : "slot"}
                    onClick={() => {
                      setSelectedSlot(slot);
                      setBookingState("idle");
                    }}
                    type="button"
                  >
                    {slot.replace("Сегодня ", "").replace("Завтра ", "")}
                  </button>
                ))}
              </div>
            </div>

            <div className="session-meta">
              <span>
                <CalendarDays size={17} />
                {answers.format}
              </span>
              <span>
                <Clock3 size={17} />
                50 минут
              </span>
            </div>

            <button className="reserve-button" onClick={() => setBookingState("saved")} type="button">
              Забронировать время
            </button>

            {bookingState === "saved" && (
              <div className="success-box" role="status">
                <Check size={18} />
                Черновик записи сохранён локально. Оплата и отправка данных отключены.
              </div>
            )}

            <div className="guarantee-box">
              <ShieldCheck size={24} />
              <div>
                <strong>Гарантия замены</strong>
                <span>Если контакт не подойдёт, в реальном продукте предложим нового специалиста.</span>
              </div>
            </div>

            <div className="prototype-box">
              <LockKeyhole size={23} />
              <div>
                <strong>Данные не отправляются в прототипе</strong>
                <span>Анкета, выборы и запись работают только в локальном интерфейсе.</span>
              </div>
            </div>
          </aside>
        </section>

        <section id="catalog" className="catalog-band">
          <div>
            <h2>Продолжить поиск в каталоге</h2>
            <p>Фильтры помогают уточнить выдачу, если первая тройка не попала в ожидания.</p>
          </div>
          <div className="filter-row" role="group" aria-label="Фильтры каталога">
            {["Все подходы", "Опыт 5+ лет", "Цена", "Формат", "Язык"].map((filter) => (
              <button
                key={filter}
                className={catalogFilter === filter ? "filter active" : "filter"}
                onClick={() => setCatalogFilter(filter)}
                type="button"
              >
                {filter}
                <ChevronDown size={15} />
              </button>
            ))}
            <button className="filter" type="button">
              <SlidersHorizontal size={15} />
              Ещё фильтры
            </button>
          </div>
        </section>

        <section id="safety" className="safety-grid">
          <SafetyItem
            icon={<UserRoundCheck size={24} />}
            title="Проверка специалистов"
            text="В прототипе показана логика бейджей. В реальном продукте документы проверяются вручную."
          />
          <SafetyItem
            icon={<ShieldCheck size={24} />}
            title="Доказательные методы"
            text="Методы и ограничения заметны в карточках, особенно для немедицинских направлений."
          />
          <SafetyItem
            icon={<LockKeyhole size={24} />}
            title="Конфиденциальность"
            text="Эта версия не отправляет данные. Позже для ПД появится отдельный защищённый слой."
          />
          <SafetyItem
            icon={<MessageSquare size={24} />}
            title="Поддержка"
            text="Жалобы, замена и супервизия заложены как будущие рабочие сценарии."
          />
        </section>

          </>
        ) : (
          registrationState.activeRole && (
            <RoleWorkspaces
              activeWorkspace={activeVisibleWorkspace}
              setActiveWorkspace={setActiveWorkspace}
              activeRole={registrationState.activeRole}
              patientProfile={patientProfile}
              setPatientProfile={setPatientProfile}
              specialistProfile={specialistProfile}
              setSpecialistProfile={setSpecialistProfile}
              adminState={adminState}
              setAdminState={setAdminState}
            />
          )
        )}
      </main>
    </div>
  );
}

function MarketplaceOverview({
  adminState,
  catalogFilter,
  marketplaceState,
  onChooseTherapist,
  onOpenRole,
  rankedTherapists,
  selectedTherapistId,
  setAdminState,
  setCatalogFilter,
  setMarketplaceState,
}: {
  adminState: AdminState;
  catalogFilter: string;
  marketplaceState: MarketplaceState;
  onChooseTherapist: (therapist: Therapist) => void;
  onOpenRole: (role: RegistrationRole, workspace?: Workspace) => void;
  rankedTherapists: Therapist[];
  selectedTherapistId: string;
  setAdminState: React.Dispatch<React.SetStateAction<AdminState>>;
  setCatalogFilter: React.Dispatch<React.SetStateAction<string>>;
  setMarketplaceState: React.Dispatch<React.SetStateAction<MarketplaceState>>;
}) {
  const [mode, setMode] = useState<MarketplaceMode>("home");
  const [clientDraft, setClientDraft] = useState<ClientSignupDraft>(initialClientSignupDraft);
  const [specialistDraft, setSpecialistDraft] = useState<SpecialistSignupDraft>(
    initialSpecialistSignupDraft,
  );
  const [lastSavedId, setLastSavedId] = useState("");
  const catalogTherapists = rankedTherapists.slice(0, 4);
  const clientServices = marketplaceServices.filter((service) => service.audience === "Клиент");

  function startClientSignup(serviceTitle: string, therapistName = "") {
    setClientDraft({
      ...initialClientSignupDraft,
      selectedService: serviceTitle,
      selectedSpecialist: therapistName,
    });
    setLastSavedId("");
    setMode("client-signup");
  }

  function startSpecialistSignup(serviceTitle = "Индивидуальные консультации") {
    setSpecialistDraft({
      ...initialSpecialistSignupDraft,
      serviceTitle,
    });
    setLastSavedId("");
    setMode("specialist-signup");
  }

  function saveClientRequest(draft: ClientSignupDraft) {
    const record: ClientMarketplaceRequest = {
      id: createRecordId("CL"),
      createdAt: formatToday(),
      selectedService: draft.selectedService,
      selectedSpecialist: draft.selectedSpecialist,
      firstName: draft.firstName,
      lastName: draft.lastName,
      dateOfBirth: draft.dateOfBirth,
      email: draft.email,
      phone: draft.phone,
      telegram: draft.telegram,
      vkId: draft.vkId,
      confirmationChannel: draft.confirmationChannel,
      contactConfirmed: draft.contactConfirmed,
      mainComplaint: draft.mainComplaint,
      therapyGoals: draft.therapyGoals,
      symptoms: draft.symptoms,
      formatPreference: draft.formatPreference,
      budget: draft.budget,
      scheduleWindows: draft.scheduleWindows,
      status: draft.contactConfirmed ? "Передана в подбор" : "Новая заявка",
    };

    setMarketplaceState((current) => ({
      ...current,
      clientRequests: [record, ...current.clientRequests].slice(0, 12),
    }));
    setLastSavedId(record.id);
  }

  function saveSpecialistApplication(draft: SpecialistSignupDraft) {
    const record: SpecialistMarketplaceApplication = {
      id: createRecordId("SP"),
      createdAt: formatToday(),
      publicName: draft.publicName,
      legalName: draft.legalName,
      email: draft.email,
      phone: draft.phone,
      telegram: draft.telegram,
      vkId: draft.vkId,
      confirmationChannel: draft.confirmationChannel,
      contactConfirmed: draft.contactConfirmed,
      professionalRole: draft.professionalRole,
      city: draft.city,
      education: draft.education,
      experienceYears: draft.experienceYears,
      serviceTitle: draft.serviceTitle,
      serviceDescription: draft.serviceDescription,
      formats: draft.formats,
      approaches: draft.approaches,
      regularSession: draft.regularSession,
      introSession: draft.introSession,
      portfolioLinks: draft.portfolioLinks,
      status: draft.contactConfirmed ? "На модерации" : "Новая заявка",
    };

    setMarketplaceState((current) => ({
      ...current,
      specialistApplications: [record, ...current.specialistApplications].slice(0, 12),
    }));
    setAdminState((current) => ({
      ...current,
      applications: [
        {
          id: record.id,
          name: record.publicName || record.legalName || "Новый специалист",
          role: record.professionalRole || record.serviceTitle,
          status: "Новая" as AdminApplication["status"],
          submitted: record.createdAt.slice(0, 5),
        },
        ...current.applications,
      ].slice(0, 12),
    }));
    setLastSavedId(record.id);
  }

  if (mode === "client-signup") {
    return (
      <ClientSignupFlow
        draft={clientDraft}
        lastSavedId={lastSavedId}
        onBack={() => setMode("home")}
        onOpenRole={onOpenRole}
        onSave={saveClientRequest}
        setDraft={setClientDraft}
        therapists={rankedTherapists.slice(0, 4)}
      />
    );
  }

  if (mode === "specialist-signup") {
    return (
      <SpecialistSignupFlow
        draft={specialistDraft}
        lastSavedId={lastSavedId}
        onBack={() => setMode("home")}
        onOpenRole={onOpenRole}
        onSave={saveSpecialistApplication}
        setDraft={setSpecialistDraft}
      />
    );
  }

  return (
    <section className="marketplace-home" aria-label="Маркетплейс MindPlace">
      <div className="marketplace-hero">
        <div className="marketplace-hero-copy">
          <h1>Выберите услугу</h1>
          <p>
            Клиент начинает с выбора услуги или специалиста, затем отвечает на вопросы, подтверждает
            контакт и создаёт заявку. Специалист регистрируется отдельно и описывает свои услуги для
            модерации.
          </p>
          <div className="hero-actions" role="group" aria-label="Быстрые входы">
            <button
              className="primary-action hero-action"
              onClick={() => startClientSignup("Первичная консультация")}
              type="button"
            >
              Я клиент
            </button>
            <button className="secondary-action" onClick={() => startSpecialistSignup()} type="button">
              Я специалист
            </button>
            <button className="secondary-action dark" onClick={() => onOpenRole("admin", "admin")} type="button">
              Администратор
            </button>
          </div>
        </div>

        <section className="marketplace-entry-panel" aria-label="Клиентский старт">
          <SectionHeader
            icon={<PackageCheck size={22} />}
            title="Что вы хотите выбрать?"
            action="Клиент"
            text="Нажмите на услугу, чтобы перейти к регистрации и вопросам."
          />
          <div className="offer-grid">
            {clientServices.map((service) => (
              <button
                className="offer-card"
                key={service.title}
                onClick={() => startClientSignup(service.title)}
                type="button"
              >
                <span>{service.price}</span>
                <strong>{service.title}</strong>
                <p>{service.text}</p>
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="marketplace-metrics">
        <Metric label="Специалисты" value={rankedTherapists.length} helper="условный каталог MVP" />
        <Metric label="Услуги" value={clientServices.length} helper="можно выбрать перед регистрацией" />
        <Metric label="Заявки клиентов" value={marketplaceState.clientRequests.length} helper="сохранены локально" />
        <Metric
          label="Заявки специалистов"
          value={marketplaceState.specialistApplications.length}
          helper={`${adminState.applications.length} в админской очереди`}
        />
      </div>

      <div className="marketplace-layout">
        <section className="dashboard-panel marketplace-panel catalog-panel wide-panel">
          <SectionHeader
            icon={<Store size={22} />}
            title="Каталог специалистов"
            action="Выбор"
            text="Клиент может выбрать специалиста и затем зарегистрироваться с уже выбранным вариантом."
          />
          <div className="filter-row marketplace-filters" role="group" aria-label="Фильтры каталога">
            {["Все подходы", "Опыт 5+ лет", "Цена", "Формат", "Язык"].map((filter) => (
              <button
                key={filter}
                className={catalogFilter === filter ? "filter active" : "filter"}
                onClick={() => setCatalogFilter(filter)}
                type="button"
              >
                {filter}
                <ChevronDown size={15} />
              </button>
            ))}
          </div>
          <div className="marketplace-catalog">
            {catalogTherapists.map((therapist) => (
              <MarketplaceTherapistTile
                key={therapist.id}
                therapist={therapist}
                active={therapist.id === selectedTherapistId}
                onSelect={() => {
                  onChooseTherapist(therapist);
                  startClientSignup("Подбор конкретного специалиста", therapist.name);
                }}
              />
            ))}
          </div>
        </section>

        <section className="dashboard-panel marketplace-panel">
          <SectionHeader
            icon={<UserRoundCheck size={22} />}
            title="Специалист предлагает услуги"
            action="Регистрация"
            text="Специалист выбирает тип услуги, описывает предложение, подтверждает контакт и отправляет заявку."
          />
          <div className="offer-grid compact-offers">
            {specialistServiceTemplates.map((service) => (
              <button
                className="offer-card"
                key={service}
                onClick={() => startSpecialistSignup(service)}
                type="button"
              >
                <span>Для каталога</span>
                <strong>{service}</strong>
                <p>Описать формат, цену, границы, опыт, документы и ссылки.</p>
              </button>
            ))}
          </div>
        </section>

        <section className="dashboard-panel marketplace-panel">
          <SectionHeader
            icon={<UsersRound size={22} />}
            title="Сохранённые записи"
            action="LocalStorage"
            text="Здесь видно, что регистрация действительно создаёт запись в демо-хранилище браузера."
          />
          <SavedMarketplaceRecords state={marketplaceState} />
        </section>

        <section className="dashboard-panel marketplace-panel">
          <SectionHeader
            icon={<ShieldCheck size={22} />}
            title="Что сохраняется в заявке"
            action="MVP"
            text="На этом слое фиксируем минимальную структуру данных перед backend."
          />
          <div className="integration-grid">
            {[
              "Выбранная услуга",
              "Личные данные",
              "Контакт и подтверждение",
              "Жалоба / описание услуги",
              "Формат и бюджет",
              "Статус обработки",
            ].map((area) => (
              <span key={area}>
                <Check size={15} />
                {area}
              </span>
            ))}
          </div>
        </section>

        <section className="dashboard-panel marketplace-panel">
          <SectionHeader
            icon={<LayoutDashboard size={22} />}
            title="Администратор видит поток"
            action="Проверка"
            text="Заявка специалиста дополнительно попадает в очередь админ-панели."
          />
          <SavedAdminPreview adminState={adminState} />
          <button className="secondary-action panel-action" onClick={() => onOpenRole("admin", "admin")} type="button">
            Открыть админ-панель
            <ArrowRight size={16} />
          </button>
        </section>
      </div>
    </section>
  );
}

function MarketplaceTherapistTile({
  therapist,
  active,
  onSelect,
}: {
  therapist: Therapist;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <article className={active ? "marketplace-therapist active" : "marketplace-therapist"}>
      <Portrait index={therapist.portraitIndex} />
      <div>
        <div className="name-line">
          <h3>{therapist.name}</h3>
          <span className="rating">
            <Star size={15} fill="currentColor" />
            {therapist.rating}
          </span>
        </div>
        <p>{therapist.title}</p>
        <span>
          Опыт {therapist.experience} лет · {formatRub(therapist.price)} ₽ · {therapist.formats.join(", ")}
        </span>
      </div>
      <button className="mini-action" onClick={onSelect} type="button">
        Выбрать и зарегистрироваться
      </button>
    </article>
  );
}

function ClientSignupFlow({
  draft,
  lastSavedId,
  onBack,
  onOpenRole,
  onSave,
  setDraft,
  therapists,
}: {
  draft: ClientSignupDraft;
  lastSavedId: string;
  onBack: () => void;
  onOpenRole: (role: RegistrationRole, workspace?: Workspace) => void;
  onSave: (draft: ClientSignupDraft) => void;
  setDraft: React.Dispatch<React.SetStateAction<ClientSignupDraft>>;
  therapists: Therapist[];
}) {
  const canSave =
    Boolean(draft.firstName.trim()) &&
    hasClientContact(draft) &&
    Boolean(draft.mainComplaint.trim()) &&
    draft.contactConfirmed &&
    draft.acceptedProcessing;

  const update = <K extends keyof ClientSignupDraft>(key: K, value: ClientSignupDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const toggleSymptom = (symptom: string) => {
    setDraft((current) => ({
      ...current,
      symptoms: current.symptoms.includes(symptom)
        ? current.symptoms.filter((item) => item !== symptom)
        : [...current.symptoms, symptom],
    }));
  };

  return (
    <section className="marketplace-signup" aria-label="Регистрация клиента">
      <SignupHeader
        action="Клиент"
        onBack={onBack}
        title="Регистрация клиента после выбора услуги"
        text="Выберите услугу или специалиста, ответьте на вопросы, укажите контакт и подтвердите его демо-кодом."
      />

      <div className="signup-grid">
        <section className="dashboard-panel wide-panel">
          <SectionHeader
            icon={<PackageCheck size={22} />}
            title="Шаг 1. Выбор услуги"
            action={draft.selectedService}
            text="Это то, с чего клиент реально начинает работу на marketplace."
          />
          <div className="offer-grid client-offer-grid">
            {marketplaceServices
              .filter((service) => service.audience === "Клиент")
              .map((service) => (
                <button
                  className={draft.selectedService === service.title ? "offer-card active" : "offer-card"}
                  key={service.title}
                  onClick={() => update("selectedService", service.title)}
                  type="button"
                >
                  <span>{service.price}</span>
                  <strong>{service.title}</strong>
                  <p>{service.text}</p>
                </button>
              ))}
          </div>
        </section>

        <section className="dashboard-panel">
          <SectionHeader
            icon={<UserRound size={22} />}
            title="Шаг 2. Личные данные"
            action="Аккаунт"
            text="Минимальные данные для создания клиентской записи."
          />
          <div className="form-grid two-cols">
            <Field label="Фамилия">
              <input value={draft.lastName} onChange={(event) => update("lastName", event.target.value)} />
            </Field>
            <Field label="Имя">
              <input value={draft.firstName} onChange={(event) => update("firstName", event.target.value)} />
            </Field>
            <Field label="Дата рождения">
              <input
                type="date"
                value={draft.dateOfBirth}
                onChange={(event) => update("dateOfBirth", event.target.value)}
              />
            </Field>
            <Field label="Предпочитаемый формат">
              <select
                value={draft.formatPreference}
                onChange={(event) => update("formatPreference", event.target.value)}
              >
                {formatOptions.map((format) => (
                  <option key={format}>{format}</option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <section className="dashboard-panel">
          <SectionHeader
            icon={<ClipboardCheck size={22} />}
            title="Шаг 3. Вопросы перед подбором"
            action="Анкета"
            text="Короткая анкета, чтобы заявка была полезной для подбора."
          />
          <div className="chip-grid compact">
            {clientSymptomOptions.map((symptom) => (
              <button
                className={draft.symptoms.includes(symptom) ? "chip selected" : "chip"}
                key={symptom}
                onClick={() => toggleSymptom(symptom)}
                type="button"
              >
                {draft.symptoms.includes(symptom) && <Check size={15} />}
                {symptom}
              </button>
            ))}
          </div>
          <div className="form-grid spaced">
            <Field label="Основная жалоба">
              <textarea
                placeholder="Что беспокоит сейчас?"
                value={draft.mainComplaint}
                onChange={(event) => update("mainComplaint", event.target.value)}
              />
            </Field>
            <Field label="Цель обращения">
              <textarea
                placeholder="Чего хотелось бы достичь?"
                value={draft.therapyGoals}
                onChange={(event) => update("therapyGoals", event.target.value)}
              />
            </Field>
            <div className="form-grid two-cols">
              <Field label="Бюджет">
                <select value={draft.budget} onChange={(event) => update("budget", event.target.value)}>
                  {budgetOptions.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </Field>
              <Field label="Удобное время">
                <input
                  placeholder="Например: будни после 19:00"
                  value={draft.scheduleWindows}
                  onChange={(event) => update("scheduleWindows", event.target.value)}
                />
              </Field>
            </div>
          </div>
        </section>

        <section className="dashboard-panel">
          <SectionHeader
            icon={<UserRoundCheck size={22} />}
            title="Шаг 4. Специалист"
            action={draft.selectedSpecialist || "Не выбран"}
            text="Можно выбрать конкретного специалиста или оставить подбор системе."
          />
          <div className="mini-choice-list">
            <button
              className={!draft.selectedSpecialist ? "choice-row active" : "choice-row"}
              onClick={() => update("selectedSpecialist", "")}
              type="button"
            >
              <strong>Подобрать автоматически</strong>
              <span>Система использует ответы анкеты.</span>
            </button>
            {therapists.map((therapist) => (
              <button
                className={draft.selectedSpecialist === therapist.name ? "choice-row active" : "choice-row"}
                key={therapist.id}
                onClick={() => update("selectedSpecialist", therapist.name)}
                type="button"
              >
                <strong>{therapist.name}</strong>
                <span>{therapist.title} · {formatRub(therapist.price)} ₽</span>
              </button>
            ))}
          </div>
        </section>

        <ContactVerificationPanel
          channel={draft.confirmationChannel}
          contactConfirmed={draft.contactConfirmed}
          email={draft.email}
          onChannel={(value) => update("confirmationChannel", value)}
          onCode={(value) => update("verificationCode", value)}
          onConfirm={() => update("contactConfirmed", draft.verificationCode.trim() === demoVerificationCode)}
          onSend={() => update("verificationSent", true)}
          onUpdate={(key, value) => update(key, value)}
          phone={draft.phone}
          telegram={draft.telegram}
          verificationCode={draft.verificationCode}
          verificationSent={draft.verificationSent}
          vkId={draft.vkId}
        />

        <section className="dashboard-panel wide-panel">
          <SectionHeader
            icon={<ShieldCheck size={22} />}
            title="Шаг 6. Сохранить запись"
            action={lastSavedId || "Черновик"}
            text="После сохранения запись появляется в локальном демо-хранилище маркетплейса."
          />
          <label className="toggle-row">
            <input
              checked={draft.acceptedProcessing}
              onChange={(event) => update("acceptedProcessing", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Согласие на обработку демо-анкеты</b>
              <small>Сейчас данные остаются только в браузере. Backend и юридические тексты подключим позже.</small>
            </span>
          </label>
          <div className="button-row">
            <button
              className="primary-action form-submit"
              disabled={!canSave}
              onClick={() => onSave(draft)}
              type="button"
            >
              Сохранить клиентскую запись
            </button>
            <button className="secondary-action" onClick={() => onOpenRole("client", "matching")} type="button">
              Перейти в подбор
            </button>
          </div>
          {!canSave && (
            <p className="form-hint">
              Нужно заполнить имя, контакт, жалобу, подтвердить демо-код и отметить согласие.
            </p>
          )}
          {lastSavedId.startsWith("CL-") && (
            <div className="success-box" role="status">
              <Check size={18} />
              Запись {lastSavedId} сохранена локально. Её видно на главной витрине в блоке сохранённых записей.
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

function SpecialistSignupFlow({
  draft,
  lastSavedId,
  onBack,
  onOpenRole,
  onSave,
  setDraft,
}: {
  draft: SpecialistSignupDraft;
  lastSavedId: string;
  onBack: () => void;
  onOpenRole: (role: RegistrationRole, workspace?: Workspace) => void;
  onSave: (draft: SpecialistSignupDraft) => void;
  setDraft: React.Dispatch<React.SetStateAction<SpecialistSignupDraft>>;
}) {
  const canSave =
    Boolean((draft.publicName || draft.legalName).trim()) &&
    Boolean(draft.serviceTitle.trim()) &&
    Boolean(draft.serviceDescription.trim()) &&
    hasSpecialistContact(draft) &&
    draft.contactConfirmed &&
    draft.acceptedVerification;

  const update = <K extends keyof SpecialistSignupDraft>(key: K, value: SpecialistSignupDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const toggleListValue = (key: "approaches" | "formats", value: string) => {
    setDraft((current) => {
      const list = current[key];
      return {
        ...current,
        [key]: list.includes(value) ? list.filter((item) => item !== value) : [...list, value],
      };
    });
  };

  return (
    <section className="marketplace-signup" aria-label="Регистрация специалиста">
      <SignupHeader
        action="Специалист"
        onBack={onBack}
        title="Регистрация специалиста и описание услуг"
        text="Специалист создаёт заявку, подтверждает контакт, описывает услуги, цены, подходы и отправляет запись на модерацию."
      />

      <div className="signup-grid">
        <section className="dashboard-panel wide-panel">
          <SectionHeader
            icon={<PackageCheck size={22} />}
            title="Шаг 1. Что вы предлагаете?"
            action={draft.serviceTitle}
            text="Выберите тип услуги или впишите свою формулировку."
          />
          <div className="offer-grid client-offer-grid">
            {specialistServiceTemplates.map((service) => (
              <button
                className={draft.serviceTitle === service ? "offer-card active" : "offer-card"}
                key={service}
                onClick={() => update("serviceTitle", service)}
                type="button"
              >
                <span>Услуга</span>
                <strong>{service}</strong>
                <p>После выбора опишите, кому и как вы помогаете.</p>
              </button>
            ))}
          </div>
          <div className="form-grid spaced">
            <Field label="Название услуги">
              <input value={draft.serviceTitle} onChange={(event) => update("serviceTitle", event.target.value)} />
            </Field>
            <Field label="Описание услуги">
              <textarea
                placeholder="Что вы предлагаете, кому это подходит, какой формат работы?"
                value={draft.serviceDescription}
                onChange={(event) => update("serviceDescription", event.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="dashboard-panel">
          <SectionHeader
            icon={<UserRoundCheck size={22} />}
            title="Шаг 2. Публичный и юридический профиль"
            action="Заявка"
            text="Данные для карточки специалиста и модерации."
          />
          <div className="form-grid two-cols">
            <Field label="Имя в каталоге">
              <input value={draft.publicName} onChange={(event) => update("publicName", event.target.value)} />
            </Field>
            <Field label="ФИО по документам">
              <input value={draft.legalName} onChange={(event) => update("legalName", event.target.value)} />
            </Field>
            <Field label="Профессиональная роль">
              <input
                placeholder="Например: психолог-консультант"
                value={draft.professionalRole}
                onChange={(event) => update("professionalRole", event.target.value)}
              />
            </Field>
            <Field label="Город">
              <input value={draft.city} onChange={(event) => update("city", event.target.value)} />
            </Field>
            <Field label="Образование">
              <textarea value={draft.education} onChange={(event) => update("education", event.target.value)} />
            </Field>
            <Field label="Опыт, лет">
              <input value={draft.experienceYears} onChange={(event) => update("experienceYears", event.target.value)} />
            </Field>
          </div>
        </section>

        <section className="dashboard-panel">
          <SectionHeader
            icon={<ListChecks size={22} />}
            title="Шаг 3. Подходы, форматы и цена"
            action="Каталог"
            text="Эти данные позже станут публичной карточкой и правилами подбора."
          />
          <div className="chip-grid compact">
            {specialistApproachOptions.map((approach) => (
              <button
                className={draft.approaches.includes(approach) ? "chip selected" : "chip"}
                key={approach}
                onClick={() => toggleListValue("approaches", approach)}
                type="button"
              >
                {draft.approaches.includes(approach) && <Check size={15} />}
                {approach}
              </button>
            ))}
          </div>
          <div className="format-row">
            {specialistFormatOptions.map((format) => (
              <button
                className={draft.formats.includes(format) ? "filter active" : "filter"}
                key={format}
                onClick={() => toggleListValue("formats", format)}
                type="button"
              >
                {format}
              </button>
            ))}
          </div>
          <div className="tariff-grid registration-tariffs">
            <NumberField label="Первичная" value={draft.introSession} onChange={(value) => update("introSession", value)} />
            <NumberField label="Сессия" value={draft.regularSession} onChange={(value) => update("regularSession", value)} />
          </div>
          <Field label="Ссылки на проекты / соцсети">
            <textarea
              placeholder="Сайт, публикации, Telegram, VK, YouTube, профессиональные сообщества"
              value={draft.portfolioLinks}
              onChange={(event) => update("portfolioLinks", event.target.value)}
            />
          </Field>
        </section>

        <ContactVerificationPanel
          channel={draft.confirmationChannel}
          contactConfirmed={draft.contactConfirmed}
          email={draft.email}
          onChannel={(value) => update("confirmationChannel", value)}
          onCode={(value) => update("verificationCode", value)}
          onConfirm={() => update("contactConfirmed", draft.verificationCode.trim() === demoVerificationCode)}
          onSend={() => update("verificationSent", true)}
          onUpdate={(key, value) => update(key, value)}
          phone={draft.phone}
          telegram={draft.telegram}
          verificationCode={draft.verificationCode}
          verificationSent={draft.verificationSent}
          vkId={draft.vkId}
        />

        <section className="dashboard-panel wide-panel">
          <SectionHeader
            icon={<ShieldCheck size={22} />}
            title="Шаг 5. Отправить заявку"
            action={lastSavedId || "Черновик"}
            text="После сохранения заявка появится в локальном списке и в очереди администратора."
          />
          <label className="toggle-row">
            <input
              checked={draft.acceptedVerification}
              onChange={(event) => update("acceptedVerification", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Согласен на проверку данных и документов</b>
              <small>Файлы в этой короткой форме не отправляются; полный документный блок уже есть в заявке специалиста.</small>
            </span>
          </label>
          <div className="button-row">
            <button
              className="primary-action form-submit"
              disabled={!canSave}
              onClick={() => onSave(draft)}
              type="button"
            >
              Сохранить заявку специалиста
            </button>
            <button className="secondary-action" onClick={() => onOpenRole("specialist", "specialist")} type="button">
              Открыть кабинет специалиста
            </button>
          </div>
          {!canSave && (
            <p className="form-hint">
              Нужно указать имя/ФИО, услугу, описание, контакт, подтвердить демо-код и согласиться на проверку.
            </p>
          )}
          {lastSavedId.startsWith("SP-") && (
            <div className="success-box" role="status">
              <Check size={18} />
              Заявка {lastSavedId} сохранена локально и добавлена в очередь администратора.
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

function ContactVerificationPanel({
  channel,
  contactConfirmed,
  email,
  onChannel,
  onCode,
  onConfirm,
  onSend,
  onUpdate,
  phone,
  telegram,
  verificationCode,
  verificationSent,
  vkId,
}: {
  channel: string;
  contactConfirmed: boolean;
  email: string;
  onChannel: (value: string) => void;
  onCode: (value: string) => void;
  onConfirm: () => void;
  onSend: () => void;
  onUpdate: (key: "email" | "phone" | "telegram" | "vkId", value: string) => void;
  phone: string;
  telegram: string;
  verificationCode: string;
  verificationSent: boolean;
  vkId: string;
}) {
  return (
    <section className="dashboard-panel">
      <SectionHeader
        icon={<MessageSquare size={22} />}
        title="Шаг 5. Контакт и подтверждение"
        action={contactConfirmed ? "Подтверждён" : "Демо-код"}
        text="В прототипе код не отправляется наружу: он показывается прямо здесь."
      />
      <div className="form-grid two-cols">
        <Field label="E-mail">
          <input value={email} onChange={(event) => onUpdate("email", event.target.value)} />
        </Field>
        <Field label="Телефон">
          <input value={phone} onChange={(event) => onUpdate("phone", event.target.value)} />
        </Field>
        <Field label="Telegram">
          <input placeholder="@username" value={telegram} onChange={(event) => onUpdate("telegram", event.target.value)} />
        </Field>
        <Field label="VK ID">
          <input placeholder="vk.com/id..." value={vkId} onChange={(event) => onUpdate("vkId", event.target.value)} />
        </Field>
        <Field label="Канал подтверждения">
          <select value={channel} onChange={(event) => onChannel(event.target.value)}>
            {contactChannelOptions.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </Field>
        <Field label="Код подтверждения">
          <input
            inputMode="numeric"
            placeholder={verificationSent ? demoVerificationCode : "Сначала отправьте код"}
            value={verificationCode}
            onChange={(event) => onCode(event.target.value)}
          />
        </Field>
      </div>
      <div className="button-row">
        <button className="secondary-action" onClick={onSend} type="button">
          Отправить демо-код
        </button>
        <button className="secondary-action" onClick={onConfirm} type="button">
          Подтвердить код
        </button>
      </div>
      {verificationSent && (
        <div className={contactConfirmed ? "success-box" : "prototype-box compact-box"} role="status">
          {contactConfirmed ? <Check size={18} /> : <LockKeyhole size={20} />}
          <div>
            <strong>{contactConfirmed ? "Контакт подтверждён" : `Демо-код: ${demoVerificationCode}`}</strong>
            <span>Введите этот код и нажмите подтверждение. Реальная отправка будет отдельной интеграцией.</span>
          </div>
        </div>
      )}
    </section>
  );
}

function SignupHeader({
  action,
  onBack,
  text,
  title,
}: {
  action: string;
  onBack: () => void;
  text: string;
  title: string;
}) {
  return (
    <div className="registration-summary signup-summary">
      <div>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
      <div className="signup-summary-actions">
        <span className="status-badge">{action}</span>
        <button className="mini-action role-exit" onClick={onBack} type="button">
          Назад к витрине
        </button>
      </div>
    </div>
  );
}

function SavedMarketplaceRecords({ state }: { state: MarketplaceState }) {
  const latestClients = state.clientRequests.slice(0, 3);
  const latestSpecialists = state.specialistApplications.slice(0, 3);

  return (
    <div className="saved-records">
      <div className="saved-record-column">
        <strong>Клиенты</strong>
        {latestClients.map((request) => (
          <article className="saved-record" key={request.id}>
            <div>
              <b>{request.id} · {request.firstName || "Без имени"}</b>
              <p>{request.selectedService} · {request.budget}</p>
              <span>{request.mainComplaint}</span>
            </div>
            <span className={`queue-status ${statusClass(request.status)}`}>{request.status}</span>
          </article>
        ))}
      </div>
      <div className="saved-record-column">
        <strong>Специалисты</strong>
        {latestSpecialists.map((application) => (
          <article className="saved-record" key={application.id}>
            <div>
              <b>{application.id} · {application.publicName || application.legalName || "Новый специалист"}</b>
              <p>{application.serviceTitle} · {application.regularSession ? `${formatRub(application.regularSession)} ₽` : "цена не указана"}</p>
              <span>{application.serviceDescription}</span>
            </div>
            <span className={`queue-status ${statusClass(application.status)}`}>{application.status}</span>
          </article>
        ))}
      </div>
    </div>
  );
}

function SavedAdminPreview({ adminState }: { adminState: AdminState }) {
  return (
    <div className="client-demo-list">
      {adminState.applications.slice(0, 3).map((application) => (
        <article className="client-demo-row" key={application.id}>
          <div>
            <strong>{application.name}</strong>
            <p>{application.role}</p>
            <span>{application.id} · подано {application.submitted}</span>
          </div>
          <span className={`queue-status ${statusClass(application.status)}`}>{application.status}</span>
        </article>
      ))}
    </div>
  );
}

function RegistrationWorkspace({
  state,
  setState,
}: {
  state: RegistrationState;
  setState: React.Dispatch<React.SetStateAction<RegistrationState>>;
}) {
  const adminCompletion = getCompletion([
    state.admin.lastName,
    state.admin.firstName,
    state.admin.dateOfBirth,
    state.admin.email,
    state.admin.phone,
    state.admin.password,
    state.admin.roleTitle,
    state.admin.accessLevel,
    state.admin.acceptedPlatformRules,
    state.admin.acceptedDataRules,
  ]);
  const specialistCompletion = getCompletion([
    state.specialist.lastName,
    state.specialist.firstName,
    state.specialist.dateOfBirth,
    state.specialist.publicName,
    state.specialist.email,
    state.specialist.phone,
    state.specialist.legalStatus,
    state.specialist.professionalRole,
    state.specialist.education,
    state.specialist.certificates,
    state.specialist.diplomaFiles,
    state.specialist.approaches,
    state.specialist.worksWith,
    state.specialist.doesNotWorkWith,
    state.specialist.bio,
    state.specialist.acceptedVerification,
  ]);
  const clientCompletion = getCompletion([
    state.client.lastName,
    state.client.firstName,
    state.client.dateOfBirth,
    state.client.nickname,
    state.client.email,
    state.client.phone,
    state.client.preferredContact,
    state.client.mainComplaint,
    state.client.therapyGoals,
    state.client.symptoms,
    state.client.formatPreference,
    state.client.budget,
    state.client.acceptedProcessing,
  ]);

  const roleCompletion: Record<RegistrationRole, ReturnType<typeof getCompletion>> = {
    admin: adminCompletion,
    specialist: specialistCompletion,
    client: clientCompletion,
  };

  if (!state.activeRole) {
    return <RoleEntryScreen setState={setState} />;
  }

  const activeRole = state.activeRole;
  const activeCompletion = roleCompletion[activeRole];
  const activeCopy = rolePortalCopy[activeRole];

  return (
    <section className="registration-workspace" aria-label="Регистрация и старт платформы">
      <div className="registration-summary">
        <div>
          <h1>{activeCopy.title}</h1>
          <p>{activeCopy.text}</p>
        </div>
      </div>

      <div className="role-scope-panel" aria-label="Текущий раздел">
        <div>
          <span>{activeCopy.progressLabel}</span>
          <strong>{activeCompletion.percent}%</strong>
          <p>
            Заполнено {activeCompletion.completed} из {activeCompletion.total}. {activeCopy.nextArea}.
          </p>
        </div>
        <div className="role-progress" aria-hidden="true">
          <i style={{ width: `${activeCompletion.percent}%` }} />
        </div>
        <button
          className="mini-action role-exit"
          onClick={() => setState((current) => ({ ...current, activeRole: "" }))}
          type="button"
        >
          Сменить вход
        </button>
      </div>

      {activeRole === "admin" && (
        <AdminRegistrationForm
          profile={state.admin}
          status={state.adminStatus}
          setState={setState}
        />
      )}
      {activeRole === "specialist" && (
        <SpecialistRegistrationForm
          profile={state.specialist}
          status={state.specialistStatus}
          setState={setState}
        />
      )}
      {activeRole === "client" && (
        <ClientRegistrationForm
          profile={state.client}
          status={state.clientStatus}
          setState={setState}
        />
      )}
    </section>
  );
}

function RoleEntryScreen({
  setState,
}: {
  setState: React.Dispatch<React.SetStateAction<RegistrationState>>;
}) {
  return (
    <section className="registration-workspace" aria-label="Выбор входа">
      <div className="registration-summary">
        <div>
          <h1>Вход в MindPlace</h1>
          <p>Выберите роль, чтобы открыть только соответствующую часть платформы.</p>
        </div>
      </div>
      <div className="role-entry-grid">
        {registrationRoleTabs.map((role) => {
          const copy = rolePortalCopy[role.id];
          return (
            <button
              className="role-entry-card"
              key={role.id}
              onClick={() => setState((current) => ({ ...current, activeRole: role.id }))}
              type="button"
            >
              <span>{role.label}</span>
              <strong>{copy.title}</strong>
              <p>{copy.nextArea}</p>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function AdminRegistrationForm({
  profile,
  status,
  setState,
}: {
  profile: AdminBootstrapProfile;
  status: RegistrationState["adminStatus"];
  setState: React.Dispatch<React.SetStateAction<RegistrationState>>;
}) {
  const update = <K extends keyof AdminBootstrapProfile>(key: K, value: AdminBootstrapProfile[K]) => {
    setState((current) => ({
      ...current,
      adminStatus: "Черновик",
      admin: { ...current.admin, [key]: value },
    }));
  };

  return (
    <div className="registration-form-grid">
      <section className="dashboard-panel">
        <SectionHeader
          icon={<LockKeyhole size={22} />}
          title="Шаг 1. Кто создаёт аккаунт?"
          action={status}
          text="Введите личные данные администратора, который получит доступ к платформе."
        />
        <div className="form-grid two-cols">
          <Field label="Фамилия">
            <input
              placeholder="Введите фамилию"
              value={profile.lastName}
              onChange={(event) => update("lastName", event.target.value)}
            />
          </Field>
          <Field label="Имя">
            <input
              placeholder="Введите имя"
              value={profile.firstName}
              onChange={(event) => update("firstName", event.target.value)}
            />
          </Field>
          <Field label="Отчество">
            <input
              placeholder="Если есть"
              value={profile.middleName}
              onChange={(event) => update("middleName", event.target.value)}
            />
          </Field>
          <Field label="Дата рождения">
            <input
              type="date"
              value={profile.dateOfBirth}
              onChange={(event) => update("dateOfBirth", event.target.value)}
            />
          </Field>
          <Field label="Должность">
            <input
              placeholder="Например: операционный администратор"
              value={profile.roleTitle}
              onChange={(event) => update("roleTitle", event.target.value)}
            />
          </Field>
          <Field label="E-mail">
            <input
              placeholder="name@example.com"
              type="email"
              value={profile.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </Field>
          <Field label="Телефон">
            <input
              placeholder="+7 000 000-00-00"
              type="tel"
              value={profile.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </Field>
          <Field label="Демо-пароль">
            <input
              placeholder="Придумайте пароль для теста"
              type="password"
              value={profile.password}
              onChange={(event) => update("password", event.target.value)}
            />
          </Field>
          <Field label="Канал 2FA">
            <select
              value={profile.twoFactorChannel}
              onChange={(event) => update("twoFactorChannel", event.target.value)}
            >
              <option value="">Выберите канал</option>
              <option>Telegram</option>
              <option>SMS</option>
              <option>E-mail</option>
              <option>Authenticator app</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<LayoutDashboard size={22} />}
          title="Шаг 2. Какие права выдать?"
          action="Bootstrap"
          text="Уровень доступа и зона ответственности администратора на marketplace."
        />
        <div className="form-grid">
          <Field label="Организация / проект">
            <input
              placeholder="Например: MindPlace"
              value={profile.organization}
              onChange={(event) => update("organization", event.target.value)}
            />
          </Field>
          <div className="form-grid two-cols">
            <Field label="Уровень доступа">
              <select value={profile.accessLevel} onChange={(event) => update("accessLevel", event.target.value)}>
                <option value="">Выберите доступ</option>
                <option>Полный доступ</option>
                <option>Операционный доступ</option>
                <option>Только модерация</option>
                <option>Только поддержка</option>
              </select>
            </Field>
            <Field label="Политика приглашений">
              <select value={profile.invitePolicy} onChange={(event) => update("invitePolicy", event.target.value)}>
                <option value="">Выберите политику</option>
                <option>Ручное приглашение специалистов после проверки</option>
                <option>Открытая форма с ручной модерацией</option>
                <option>Только по реферальной ссылке</option>
              </select>
            </Field>
          </div>
          <Field label="Заметки к запуску">
            <textarea
              placeholder="Что важно знать команде перед запуском этой роли?"
              value={profile.launchNotes}
              onChange={(event) => update("launchNotes", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<ShieldCheck size={22} />}
          title="Шаг 3. Подтверждения"
          action="До продакшена"
          text="Пока это локальный прототип, но поля фиксируют будущие обязательные подтверждения."
        />
        <div className="registration-consent-grid">
          <label className="toggle-row">
            <input
              checked={profile.acceptedPlatformRules}
              onChange={(event) => update("acceptedPlatformRules", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Администратор принимает правила платформы</b>
              <small>В реальном продукте здесь будут роли, журнал действий и политика модерации.</small>
            </span>
          </label>
          <label className="toggle-row">
            <input
              checked={profile.acceptedDataRules}
              onChange={(event) => update("acceptedDataRules", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Администратор принимает правила обработки данных</b>
              <small>Позже это уйдёт в юридический и инфраструктурный слой.</small>
            </span>
          </label>
        </div>
        <div className="setup-checklist">
          {["Создать доступ", "Назначить уровень", "Проверять заявки", "Координировать поддержку"].map(
            (item) => (
              <span key={item}>
                <Check size={16} />
                {item}
              </span>
            ),
          )}
        </div>
        <div className="button-row">
          <button
            className="primary-action form-submit"
            onClick={() => setState((current) => ({ ...current, adminStatus: "Готов к запуску" }))}
            type="button"
          >
            Пометить администратора готовым
          </button>
        </div>
      </section>
    </div>
  );
}

function SpecialistRegistrationForm({
  profile,
  status,
  setState,
}: {
  profile: SpecialistRegistration;
  status: RegistrationState["specialistStatus"];
  setState: React.Dispatch<React.SetStateAction<RegistrationState>>;
}) {
  const update = <K extends keyof SpecialistRegistration>(key: K, value: SpecialistRegistration[K]) => {
    setState((current) => ({
      ...current,
      specialistStatus: "Черновик",
      specialist: { ...current.specialist, [key]: value },
    }));
  };

  const updateFiles = (
    key: "diplomaFiles" | "certificateFiles" | "licenseFiles",
    files: FileList | null,
  ) => {
    update(key, files ? Array.from(files).map((file) => file.name) : []);
  };

  const toggleListValue = (key: "approaches" | "formats", value: string) => {
    setState((current) => {
      const list = current.specialist[key];
      return {
        ...current,
        specialistStatus: "Черновик",
        specialist: {
          ...current.specialist,
          [key]: list.includes(value) ? list.filter((item) => item !== value) : [...list, value],
        },
      };
    });
  };

  return (
    <div className="registration-form-grid">
      <section className="dashboard-panel">
        <SectionHeader
          icon={<UserRoundCheck size={22} />}
          title="Шаг 1. Личные данные"
          action={status}
          text="Введите данные специалиста, которые нужны для связи и проверки заявки."
        />
        <div className="form-grid two-cols">
          <Field label="Фамилия">
            <input
              placeholder="Введите фамилию"
              value={profile.lastName}
              onChange={(event) => update("lastName", event.target.value)}
            />
          </Field>
          <Field label="Имя">
            <input
              placeholder="Введите имя"
              value={profile.firstName}
              onChange={(event) => update("firstName", event.target.value)}
            />
          </Field>
          <Field label="Отчество">
            <input
              placeholder="Если есть"
              value={profile.middleName}
              onChange={(event) => update("middleName", event.target.value)}
            />
          </Field>
          <Field label="Дата рождения">
            <input
              type="date"
              value={profile.dateOfBirth}
              onChange={(event) => update("dateOfBirth", event.target.value)}
            />
          </Field>
          <Field label="Имя в каталоге">
            <input
              placeholder="Как показывать вас клиентам"
              value={profile.publicName}
              onChange={(event) => update("publicName", event.target.value)}
            />
          </Field>
          <Field label="E-mail">
            <input
              placeholder="name@example.com"
              type="email"
              value={profile.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </Field>
          <Field label="Телефон">
            <input
              placeholder="+7 000 000-00-00"
              type="tel"
              value={profile.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </Field>
          <Field label="Город">
            <input placeholder="Город практики" value={profile.city} onChange={(event) => update("city", event.target.value)} />
          </Field>
          <Field label="Часовой пояс">
            <input placeholder="Например: GMT+3" value={profile.timezone} onChange={(event) => update("timezone", event.target.value)} />
          </Field>
          <Field label="Юридический статус">
            <select value={profile.legalStatus} onChange={(event) => update("legalStatus", event.target.value)}>
              <option value="">Выберите статус</option>
              <option>Самозанятый</option>
              <option>ИП</option>
              <option>Физическое лицо</option>
              <option>ООО / клиника</option>
            </select>
          </Field>
          <Field label="Профессиональная роль">
            <input
              placeholder="Например: психолог-консультант"
              value={profile.professionalRole}
              onChange={(event) => update("professionalRole", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<FileCheck2 size={22} />}
          title="Шаг 2. Квалификация"
          action="Проверка"
          text="Образование, сертификаты и практический опыт для модерации анкеты."
        />
        <div className="form-grid">
          <Field label="Образование">
            <textarea
              placeholder="ВУЗ, факультет, специальность, год окончания"
              value={profile.education}
              onChange={(event) => update("education", event.target.value)}
            />
          </Field>
          <Field label="Степень / квалификация">
            <input
              placeholder="Например: магистр психологии"
              value={profile.degree}
              onChange={(event) => update("degree", event.target.value)}
            />
          </Field>
          <Field label="Сертификаты и программы">
            <textarea
              placeholder="Курсы, программы, супервизия, подтверждающие документы"
              value={profile.certificates}
              onChange={(event) => update("certificates", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<UploadCloud size={22} />}
          title="Шаг 3. Документы"
          action="Демо-файлы"
          text="Файлы не отправляются на сервер: в прототипе сохраняются только их названия."
        />
        <div className="upload-grid">
          <FileUpload
            label="Дипломы"
            helper="PDF, JPG, PNG"
            files={profile.diplomaFiles}
            onFiles={(files) => updateFiles("diplomaFiles", files)}
          />
          <FileUpload
            label="Сертификаты"
            helper="Курсы, программы, супервизии"
            files={profile.certificateFiles}
            onFiles={(files) => updateFiles("certificateFiles", files)}
          />
          <FileUpload
            label="Лицензии / допуски"
            helper="Если применимо"
            files={profile.licenseFiles}
            onFiles={(files) => updateFiles("licenseFiles", files)}
          />
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<ListChecks size={22} />}
          title="Шаг 4. Опыт и практика"
          action="Профиль"
          text="Расскажите, сколько практики было и как устроена профессиональная поддержка."
        />
        <div className="form-grid two-cols">
          <Field label="Опыт, лет">
            <input
              placeholder="Например: 5"
              value={profile.experienceYears}
              onChange={(event) => update("experienceYears", event.target.value)}
            />
          </Field>
          <Field label="Практических часов">
            <input
              placeholder="Примерная оценка"
              value={profile.practiceHours}
              onChange={(event) => update("practiceHours", event.target.value)}
            />
          </Field>
          <Field label="Супервизия">
            <textarea
              placeholder="Как часто, в каком формате, у кого"
              value={profile.supervision}
              onChange={(event) => update("supervision", event.target.value)}
            />
          </Field>
          <Field label="Личная терапия">
            <textarea
              placeholder="Если применимо: часы, формат, период"
              value={profile.personalTherapy}
              onChange={(event) => update("personalTherapy", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<ClipboardCheck size={22} />}
          title="Шаг 5. Подходы, темы и границы"
          action="Важно"
          text="Это база для будущего подбора и для прозрачной карточки специалиста."
        />
        <div className="chip-grid compact">
          {specialistApproachOptions.map((approach) => (
            <button
              key={approach}
              className={profile.approaches.includes(approach) ? "chip selected" : "chip"}
              onClick={() => toggleListValue("approaches", approach)}
              type="button"
            >
              {profile.approaches.includes(approach) && <Check size={15} />}
              {approach}
            </button>
          ))}
        </div>
        <div className="form-grid two-cols spaced">
          <Field label="Темы и специализация">
            <textarea
              placeholder="С какими запросами вы чаще всего работаете?"
              value={profile.topics}
              onChange={(event) => update("topics", event.target.value)}
            />
          </Field>
          <Field label="Языки">
            <input
              placeholder="Например: русский, английский B2"
              value={profile.languages}
              onChange={(event) => update("languages", event.target.value)}
            />
          </Field>
          <Field label="Работаю с">
            <textarea
              placeholder="Возраст, запросы, состояния, форматы"
              value={profile.worksWith}
              onChange={(event) => update("worksWith", event.target.value)}
            />
          </Field>
          <Field label="Не работаю с">
            <textarea
              placeholder="Острые состояния, зависимости, темы вне вашей компетенции"
              value={profile.doesNotWorkWith}
              onChange={(event) => update("doesNotWorkWith", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<WalletCards size={22} />}
          title="Шаг 6. Стоимость и форматы"
          action="Черновик"
          text="Ценообразование оставляем гибким, чтобы позже проверить модели."
        />
        <div className="format-row">
          {specialistFormatOptions.map((format) => (
            <button
              key={format}
              className={profile.formats.includes(format) ? "filter active" : "filter"}
              onClick={() => toggleListValue("formats", format)}
              type="button"
            >
              {format}
            </button>
          ))}
        </div>
        <div className="tariff-grid registration-tariffs">
          <NumberField label="Первичная" value={profile.introSession} onChange={(value) => update("introSession", value)} />
          <NumberField label="Сессия" value={profile.regularSession} onChange={(value) => update("regularSession", value)} />
          <NumberField label="Пакет 4" value={profile.packageFour} onChange={(value) => update("packageFour", value)} />
          <NumberField label="Пакет 8" value={profile.packageEight} onChange={(value) => update("packageEight", value)} />
          <NumberField label="Льготное окно" value={profile.socialSlot} onChange={(value) => update("socialSlot", value)} />
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<MessageSquare size={22} />}
          title="Шаг 7. Ссылки и публичная карточка"
          action="Каталог"
          text="То, что поможет администратору и будущему клиенту проверить специалиста."
        />
        <div className="form-grid">
          <Field label="Проекты / публикации">
            <textarea
              placeholder="Ссылки на сайт, публикации, профессиональные профили"
              value={profile.portfolioLinks}
              onChange={(event) => update("portfolioLinks", event.target.value)}
            />
          </Field>
          <Field label="Социальные сети">
            <textarea
              placeholder="Telegram, VK, YouTube, профессиональные сообщества"
              value={profile.socialLinks}
              onChange={(event) => update("socialLinks", event.target.value)}
            />
          </Field>
          <Field label="Ссылка на intro-видео">
            <input
              placeholder="https://..."
              value={profile.introVideoUrl}
              onChange={(event) => update("introVideoUrl", event.target.value)}
            />
          </Field>
          <Field label="О себе">
            <textarea
              placeholder="Коротко о стиле работы и том, чего клиент может ожидать"
              value={profile.bio}
              onChange={(event) => update("bio", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<ShieldCheck size={22} />}
          title="Шаг 8. Согласия и отправка заявки"
          action="Локально"
          text="Отправка меняет только статус демо-заявки. Реального backend сейчас нет."
        />
        <div className="registration-consent-grid">
          <label className="toggle-row">
            <input
              checked={profile.acceptedVerification}
              onChange={(event) => update("acceptedVerification", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Согласен на проверку документов</b>
              <small>В реальном продукте это будет отдельный юридический текст.</small>
            </span>
          </label>
          <label className="toggle-row">
            <input
              checked={profile.acceptedPlatformRules}
              onChange={(event) => update("acceptedPlatformRules", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Согласен с правилами платформы</b>
              <small>Включая модерацию профиля, границы практики и правила коммуникации.</small>
            </span>
          </label>
        </div>
        <Field label="Комментарий для модератора">
          <textarea
            placeholder="Что важно знать администратору при проверке заявки?"
            value={profile.moderationComment}
            onChange={(event) => update("moderationComment", event.target.value)}
          />
        </Field>
        <div className="button-row">
          <button
            className="primary-action form-submit"
            onClick={() =>
              setState((current) => ({ ...current, specialistStatus: "Отправлено на модерацию" }))
            }
            type="button"
          >
            Отправить демо-заявку специалиста
          </button>
        </div>
      </section>
    </div>
  );
}

function ClientRegistrationForm({
  profile,
  status,
  setState,
}: {
  profile: ClientRegistration;
  status: RegistrationState["clientStatus"];
  setState: React.Dispatch<React.SetStateAction<RegistrationState>>;
}) {
  const update = <K extends keyof ClientRegistration>(key: K, value: ClientRegistration[K]) => {
    setState((current) => ({
      ...current,
      clientStatus: "Черновик",
      client: { ...current.client, [key]: value },
    }));
  };

  const toggleSymptom = (symptom: string) => {
    setState((current) => {
      const exists = current.client.symptoms.includes(symptom);
      return {
        ...current,
        clientStatus: "Черновик",
        client: {
          ...current.client,
          symptoms: exists
            ? current.client.symptoms.filter((item) => item !== symptom)
            : [...current.client.symptoms, symptom],
        },
      };
    });
  };

  return (
    <div className="registration-form-grid">
      <section className="dashboard-panel">
        <SectionHeader
          icon={<UserRound size={22} />}
          title="Шаг 1. Личные данные"
          action={status}
          text="Введите данные, которые нужны для создания клиентского кабинета."
        />
        <div className="form-grid two-cols">
          <Field label="Фамилия">
            <input
              placeholder="Введите фамилию"
              value={profile.lastName}
              onChange={(event) => update("lastName", event.target.value)}
            />
          </Field>
          <Field label="Имя">
            <input
              placeholder="Введите имя"
              value={profile.firstName}
              onChange={(event) => update("firstName", event.target.value)}
            />
          </Field>
          <Field label="Отчество">
            <input
              placeholder="Если есть"
              value={profile.middleName}
              onChange={(event) => update("middleName", event.target.value)}
            />
          </Field>
          <Field label="Дата рождения">
            <input
              type="date"
              value={profile.dateOfBirth}
              onChange={(event) => update("dateOfBirth", event.target.value)}
            />
          </Field>
          <Field label="Имя или ник в кабинете">
            <input
              placeholder="Как обращаться к вам в кабинете"
              value={profile.nickname}
              onChange={(event) => update("nickname", event.target.value)}
            />
          </Field>
          <Field label="Возраст">
            <input placeholder="Например: 34" value={profile.age} onChange={(event) => update("age", event.target.value)} />
          </Field>
          <Field label="Город">
            <input placeholder="Ваш город" value={profile.city} onChange={(event) => update("city", event.target.value)} />
          </Field>
          <Field label="E-mail">
            <input
              placeholder="name@example.com"
              type="email"
              value={profile.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </Field>
          <Field label="Телефон">
            <input
              placeholder="+7 000 000-00-00"
              type="tel"
              value={profile.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </Field>
          <Field label="Часовой пояс">
            <input placeholder="Например: GMT+3" value={profile.timezone} onChange={(event) => update("timezone", event.target.value)} />
          </Field>
          <Field label="Канал связи">
            <select value={profile.preferredContact} onChange={(event) => update("preferredContact", event.target.value)}>
              <option value="">Выберите канал</option>
              <option>Telegram</option>
              <option>E-mail</option>
              <option>SMS</option>
              <option>Только в кабинете</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<ClipboardCheck size={22} />}
          title="Шаг 2. Жалобы и цель обращения"
          action="Анкета"
          text="Эти поля потом станут основой первичного подбора и личного кабинета."
        />
        <div className="form-grid">
          <Field label="Основная жалоба">
            <textarea
              placeholder="Что беспокоит сейчас? Можно своими словами."
              value={profile.mainComplaint}
              onChange={(event) => update("mainComplaint", event.target.value)}
            />
          </Field>
          <Field label="Как давно это беспокоит">
            <input
              placeholder="Например: 3 месяца, около года"
              value={profile.complaintDuration}
              onChange={(event) => update("complaintDuration", event.target.value)}
            />
          </Field>
          <Field label="Цели терапии">
            <textarea
              placeholder="Что вы хотели бы изменить или понять?"
              value={profile.therapyGoals}
              onChange={(event) => update("therapyGoals", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<ListChecks size={22} />}
          title="Шаг 3. Состояние и история"
          action="Чек-лист"
          text="Нужен не для диагноза, а для бережного маршрута и ограничения рисков."
        />
        <div className="chip-grid compact">
          {clientSymptomOptions.map((symptom) => (
            <button
              key={symptom}
              className={profile.symptoms.includes(symptom) ? "chip selected" : "chip"}
              onClick={() => toggleSymptom(symptom)}
              type="button"
            >
              {profile.symptoms.includes(symptom) && <Check size={15} />}
              {symptom}
            </button>
          ))}
        </div>
        <div className="form-grid spaced">
          <Field label="Предыдущий опыт терапии">
            <textarea
              placeholder="Был ли опыт терапии, что подошло или не подошло?"
              value={profile.priorTherapy}
              onChange={(event) => update("priorTherapy", event.target.value)}
            />
          </Field>
          <Field label="Лекарства / наблюдение у врача">
            <textarea
              placeholder="Если есть важная медицинская информация, укажите её"
              value={profile.medicationOrDoctor}
              onChange={(event) => update("medicationOrDoctor", event.target.value)}
            />
          </Field>
          <Field label="Риски или важные ограничения">
            <textarea
              placeholder="Что специалисту важно учитывать с самого начала?"
              value={profile.riskNotes}
              onChange={(event) => update("riskNotes", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<CalendarDays size={22} />}
          title="Шаг 4. Предпочтения по работе"
          action="Подбор"
          text="Формат, бюджет и окна времени, которые потом отправятся в matching."
        />
        <div className="form-grid two-cols">
          <Field label="Формат">
            <select
              value={profile.formatPreference}
              onChange={(event) => update("formatPreference", event.target.value)}
            >
              <option value="">Выберите формат</option>
              <option>Видео</option>
              <option>Аудио</option>
              <option>Чат</option>
              <option>Очно</option>
            </select>
          </Field>
          <Field label="Бюджет">
            <select value={profile.budget} onChange={(event) => update("budget", event.target.value)}>
              <option value="">Выберите бюджет</option>
              {budgetOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </Field>
          <Field label="Предпочтения к специалисту">
            <textarea
              placeholder="Пол, опыт, подход, стиль общения, что важно для контакта"
              value={profile.therapistPreference}
              onChange={(event) => update("therapistPreference", event.target.value)}
            />
          </Field>
          <Field label="Удобное расписание">
            <textarea
              placeholder="Например: будни после 19:00, выходные днём"
              value={profile.scheduleWindows}
              onChange={(event) => update("scheduleWindows", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<ShieldCheck size={22} />}
          title="Шаг 5. Конфиденциальность и согласия"
          action="Локально"
          text="Сейчас все данные вымышленные и остаются в браузере; это только модель формы."
        />
        <div className="registration-consent-grid">
          <label className="toggle-row">
            <input
              checked={profile.privacyMode}
              onChange={(event) => update("privacyMode", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Показывать специалисту ник до подтверждения записи</b>
              <small>Полезно для будущего режима приватности.</small>
            </span>
          </label>
          <label className="toggle-row">
            <input
              checked={profile.acceptedProcessing}
              onChange={(event) => update("acceptedProcessing", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Согласие на обработку анкеты в демо</b>
              <small>В продакшене потребуется полноценное юридическое согласие.</small>
            </span>
          </label>
          <label className="toggle-row">
            <input
              checked={profile.acceptedPlatformRules}
              onChange={(event) => update("acceptedPlatformRules", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Согласие с правилами платформы</b>
              <small>Коммуникация, отмены, замена специалиста, границы сервиса.</small>
            </span>
          </label>
          <Field label="Экстренный контакт или важная заметка">
            <textarea
              placeholder="Необязательное поле для важной информации"
              value={profile.emergencyContact}
              onChange={(event) => update("emergencyContact", event.target.value)}
            />
          </Field>
        </div>
        <div className="button-row">
          <button
            className="primary-action form-submit"
            onClick={() => setState((current) => ({ ...current, clientStatus: "Анкета сохранена" }))}
            type="button"
          >
            Сохранить демо-анкету клиента
          </button>
        </div>
      </section>
    </div>
  );
}

function FileUpload({
  label,
  helper,
  files,
  onFiles,
}: {
  label: string;
  helper: string;
  files: string[];
  onFiles: (files: FileList | null) => void;
}) {
  return (
    <div className="upload-field">
      <span>{label}</span>
      <label className="upload-box">
        <input multiple type="file" onChange={(event) => onFiles(event.target.files)} />
        <UploadCloud size={21} />
        <strong>Выбрать файлы</strong>
        <small>{helper}</small>
      </label>
      <div className="upload-list">
        {files.length ? files.map((file) => <span key={file}>{file}</span>) : <em>Файлы пока не выбраны</em>}
      </div>
    </div>
  );
}

function RoleWorkspaces({
  activeWorkspace,
  setActiveWorkspace,
  activeRole,
  patientProfile,
  setPatientProfile,
  specialistProfile,
  setSpecialistProfile,
  adminState,
  setAdminState,
}: {
  activeWorkspace: Exclude<Workspace, "matching" | "registration">;
  setActiveWorkspace: React.Dispatch<React.SetStateAction<Workspace>>;
  activeRole: RegistrationRole;
  patientProfile: PatientProfile;
  setPatientProfile: React.Dispatch<React.SetStateAction<PatientProfile>>;
  specialistProfile: SpecialistProfile;
  setSpecialistProfile: React.Dispatch<React.SetStateAction<SpecialistProfile>>;
  adminState: AdminState;
  setAdminState: React.Dispatch<React.SetStateAction<AdminState>>;
}) {
  const cabinetTabs = getRoleWorkspaceTabs(activeRole).filter(
    (tab) => tab.id !== "matching" && tab.id !== "registration",
  );
  const title =
    activeRole === "client"
      ? "Кабинет клиента"
      : activeRole === "specialist"
        ? "Кабинет специалиста"
        : "Административная панель";
  const description =
    activeRole === "client"
      ? "Клиент видит свой профиль, запрос, запись, состояние и запрос на замену специалиста."
      : activeRole === "specialist"
        ? "Специалист видит только свой профиль, документы, подходы, тарифы и расписание."
        : "Администратор координирует заявки, бронирования, жалобы, замены и платежный контур.";

  return (
    <section className="role-workspace" aria-label="Рабочие кабинеты">
      <div className="workspace-summary">
        <div>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {cabinetTabs.length > 1 && (
          <div className="workspace-switcher" role="group" aria-label="Переключить кабинет">
            {cabinetTabs.map((tab) => (
              <button
                key={tab.id}
                className={activeWorkspace === tab.id ? "active" : ""}
                onClick={() => setActiveWorkspace(tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {activeWorkspace === "patient" && (
        <PatientCabinet profile={patientProfile} setProfile={setPatientProfile} />
      )}
      {activeWorkspace === "specialist" && (
        <SpecialistCabinet profile={specialistProfile} setProfile={setSpecialistProfile} />
      )}
      {activeWorkspace === "admin" && (
        <AdminCabinet state={adminState} setState={setAdminState} />
      )}
    </section>
  );
}

function PatientCabinet({
  profile,
  setProfile,
}: {
  profile: PatientProfile;
  setProfile: React.Dispatch<React.SetStateAction<PatientProfile>>;
}) {
  const update = <K extends keyof PatientProfile>(key: K, value: PatientProfile[K]) => {
    setProfile((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="cabinet-grid patient-grid">
      <section className="dashboard-panel">
        <SectionHeader
          icon={<UserRound size={22} />}
          title="Профиль пациента"
          action="Локально"
          text="Минимальный профиль для будущего личного кабинета."
        />
        <div className="form-grid two-cols">
          <Field label="Имя или ник">
            <input value={profile.displayName} onChange={(event) => update("displayName", event.target.value)} />
          </Field>
          <Field label="Возраст">
            <input value={profile.age} onChange={(event) => update("age", event.target.value)} />
          </Field>
          <Field label="Город">
            <input value={profile.city} onChange={(event) => update("city", event.target.value)} />
          </Field>
          <Field label="Часовой пояс">
            <input value={profile.timezone} onChange={(event) => update("timezone", event.target.value)} />
          </Field>
          <Field label="Канал уведомлений">
            <select
              value={profile.preferredContact}
              onChange={(event) => update("preferredContact", event.target.value)}
            >
              <option>Telegram</option>
              <option>E-mail</option>
              <option>SMS</option>
              <option>Только в кабинете</option>
            </select>
          </Field>
          <label className="toggle-row">
            <input
              checked={profile.privacyMode}
              onChange={(event) => update("privacyMode", event.target.checked)}
              type="checkbox"
            />
            <span>
              <b>Анонимный режим</b>
              <small>Показывать специалисту ник до подтверждения записи.</small>
            </span>
          </label>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<ClipboardCheck size={22} />}
          title="Текущий запрос"
          action="Черновик"
          text="Поля будущей анкеты, которую можно дорабатывать перед подбором."
        />
        <div className="form-grid">
          <Field label="Запрос">
            <input
              value={profile.currentRequest}
              onChange={(event) => update("currentRequest", event.target.value)}
            />
          </Field>
          <Field label="Цель работы">
            <textarea value={profile.therapyGoal} onChange={(event) => update("therapyGoal", event.target.value)} />
          </Field>
          <div className="form-grid three-cols">
            <Field label="Формат">
              <select
                value={profile.preferredFormat}
                onChange={(event) => update("preferredFormat", event.target.value)}
              >
                <option>Видео</option>
                <option>Чат</option>
                <option>Аудио</option>
              </select>
            </Field>
            <Field label="Бюджет">
              <select value={profile.budget} onChange={(event) => update("budget", event.target.value)}>
                {budgetOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </Field>
            <Field label="Ритм">
              <select
                value={profile.sessionCadence}
                onChange={(event) => update("sessionCadence", event.target.value)}
              >
                <option>1 раз в неделю</option>
                <option>2 раза в неделю</option>
                <option>Раз в две недели</option>
                <option>По необходимости</option>
              </select>
            </Field>
          </div>
          <Field label="Предпочтения к специалисту">
            <input
              value={profile.specialistPreference}
              onChange={(event) => update("specialistPreference", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<CalendarDays size={22} />}
          title="Ближайшая сессия"
          action="Подтверждена"
          text="Демо-сценарий записи без оплаты и отправки данных."
        />
        <div className="session-row">
          <Portrait index={4} />
          <div>
            <strong>Мария Волкова</strong>
            <span>ACT, CFT · психолог</span>
          </div>
          <b>Сегодня 19:00</b>
        </div>
        <div className="button-row">
          <button className="secondary-action" type="button">
            <MessageSquare size={17} />
            Написать
          </button>
          <button className="secondary-action" type="button">
            Перенести
          </button>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<LineChart size={22} />}
          title="Трекер состояния"
          action="Сегодня"
          text="Локальный check-in для будущей динамики."
        />
        <div className="mood-scale" role="group" aria-label="Уровень напряжения">
          {[1, 2, 3, 4, 5].map((score) => (
            <button
              key={score}
              className={profile.moodScore === score ? "mood active" : "mood"}
              onClick={() => update("moodScore", score)}
              type="button"
            >
              {score}
            </button>
          ))}
        </div>
        <div className="chip-grid compact">
          {["Спокойствие", "Усталость", "Тревога", "Грусть", "Раздражение", "Радость"].map((state) => (
            <button
              key={state}
              className={profile.emotionalState === state ? "chip selected" : "chip"}
              onClick={() => update("emotionalState", state)}
              type="button"
            >
              {profile.emotionalState === state && <Check size={15} />}
              {state}
            </button>
          ))}
        </div>
      </section>

      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<ShieldCheck size={22} />}
          title="Запрос на замену"
          action="Будущий workflow"
          text="Проверяем, как клиент сможет описать проблему и получить альтернативы."
        />
        <div className="replacement-grid">
          <Field label="Причина, если хотите указать">
            <textarea
              placeholder="Например: не совпал стиль работы, сложно выбрать время, не почувствовал(а) контакт."
              value={profile.replacementReason}
              onChange={(event) => update("replacementReason", event.target.value)}
            />
          </Field>
          <div className="promise-list">
            <span>
              <Clock3 size={18} />
              Обычно 1-2 дня
            </span>
            <span>
              <LockKeyhole size={18} />
              Видно только команде
            </span>
            <span>
              <Sparkles size={18} />
              Подберём альтернативу
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

function SpecialistCabinet({
  profile,
  setProfile,
}: {
  profile: SpecialistProfile;
  setProfile: React.Dispatch<React.SetStateAction<SpecialistProfile>>;
}) {
  const update = <K extends keyof SpecialistProfile>(key: K, value: SpecialistProfile[K]) => {
    setProfile((current) => ({ ...current, [key]: value }));
  };
  const updatePricing = <K extends keyof PricingModel>(key: K, value: PricingModel[K]) => {
    setProfile((current) => ({
      ...current,
      pricing: { ...current.pricing, [key]: value },
    }));
  };

  function toggleListValue(key: "approaches" | "formats", value: string) {
    setProfile((current) => {
      const list = current[key];
      return {
        ...current,
        [key]: list.includes(value) ? list.filter((item) => item !== value) : [...list, value],
      };
    });
  }

  return (
    <div className="cabinet-grid specialist-grid">
      <section className="dashboard-panel">
        <SectionHeader
          icon={<BriefcaseBusiness size={22} />}
          title="Публичный профиль"
          action={profile.moderationStatus}
          text="То, что потом попадёт в карточку специалиста."
        />
        <div className="form-grid two-cols">
          <Field label="Имя в каталоге">
            <input value={profile.publicName} onChange={(event) => update("publicName", event.target.value)} />
          </Field>
          <Field label="Уровень">
            <select value={profile.level} onChange={(event) => update("level", event.target.value)}>
              <option>junior</option>
              <option>middle</option>
              <option>senior</option>
              <option>expert</option>
            </select>
          </Field>
          <Field label="Профессиональная строка">
            <input
              value={profile.professionalTitle}
              onChange={(event) => update("professionalTitle", event.target.value)}
            />
          </Field>
          <Field label="Город">
            <input value={profile.city} onChange={(event) => update("city", event.target.value)} />
          </Field>
          <Field label="Опыт, лет">
            <input
              value={profile.experienceYears}
              onChange={(event) => update("experienceYears", event.target.value)}
            />
          </Field>
          <Field label="Статус модерации">
            <select
              value={profile.moderationStatus}
              onChange={(event) =>
                update("moderationStatus", event.target.value as SpecialistProfile["moderationStatus"])
              }
            >
              <option>Черновик</option>
              <option>На проверке</option>
              <option>Одобрен</option>
            </select>
          </Field>
        </div>
        <div className="completion-box">
          <span>Заполненность профиля</span>
          <b>{profile.profileCompleteness}%</b>
          <div>
            <i style={{ width: `${profile.profileCompleteness}%` }} />
          </div>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<FileCheck2 size={22} />}
          title="Образование и документы"
          action="Проверка"
          text="Пока как текстовые поля; загрузку документов подключим позже."
        />
        <div className="form-grid">
          <Field label="Образование">
            <textarea value={profile.education} onChange={(event) => update("education", event.target.value)} />
          </Field>
          <Field label="Сертификаты / супервизия">
            <textarea value={profile.certificates} onChange={(event) => update("certificates", event.target.value)} />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<ListChecks size={22} />}
          title="Подходы и границы"
          action="В каталоге"
          text="Важно явно показывать, с чем специалист работает и с чем нет."
        />
        <div className="chip-grid compact">
          {["КПТ", "ACT", "EMDR", "Схема-терапия", "CFT", "Гештальт"].map((approach) => (
            <button
              key={approach}
              className={profile.approaches.includes(approach) ? "chip selected" : "chip"}
              onClick={() => toggleListValue("approaches", approach)}
              type="button"
            >
              {profile.approaches.includes(approach) && <Check size={15} />}
              {approach}
            </button>
          ))}
        </div>
        <div className="form-grid two-cols spaced">
          <Field label="Работаю с">
            <textarea value={profile.worksWith} onChange={(event) => update("worksWith", event.target.value)} />
          </Field>
          <Field label="Не работаю с">
            <textarea
              value={profile.doesNotWorkWith}
              onChange={(event) => update("doesNotWorkWith", event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-panel">
        <SectionHeader
          icon={<WalletCards size={22} />}
          title="Тарифы"
          action="Гибкая модель"
          text="Цены пока обсуждаемые, поэтому заложены отдельными полями."
        />
        <div className="tariff-grid">
          <NumberField label="Первичная" value={profile.pricing.introSession} onChange={(value) => updatePricing("introSession", value)} />
          <NumberField label="Сессия" value={profile.pricing.regularSession} onChange={(value) => updatePricing("regularSession", value)} />
          <NumberField label="Пакет 4" value={profile.pricing.packageFour} onChange={(value) => updatePricing("packageFour", value)} />
          <NumberField label="Пакет 8" value={profile.pricing.packageEight} onChange={(value) => updatePricing("packageEight", value)} />
          <NumberField label="Льготное окно" value={profile.pricing.socialSlot} onChange={(value) => updatePricing("socialSlot", value)} />
          <NumberField
            label="Комиссия, %"
            value={profile.pricing.platformCommission}
            onChange={(value) => updatePricing("platformCommission", value)}
          />
        </div>
        <NumberField
          label="Бесплатная отмена, часов"
          value={profile.pricing.cancellationWindowHours}
          onChange={(value) => updatePricing("cancellationWindowHours", value)}
        />
      </section>

      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<CalendarDays size={22} />}
          title="Расписание на неделю"
          action="Демо"
          text="Слоты отображаются как будущая сетка доступности."
        />
        <div className="format-row">
          {["Видео", "Чат", "Аудио"].map((format) => (
            <button
              key={format}
              className={profile.formats.includes(format) ? "filter active" : "filter"}
              onClick={() => toggleListValue("formats", format)}
              type="button"
            >
              {format}
            </button>
          ))}
        </div>
        <ScheduleBoard />
      </section>
    </div>
  );
}

function AdminCabinet({
  state,
  setState,
}: {
  state: AdminState;
  setState: React.Dispatch<React.SetStateAction<AdminState>>;
}) {
  const applicationCounts = {
    all: state.applications.length,
    review: state.applications.filter((item) => item.status !== "Одобрить").length,
  };
  const bookingRevenue = state.bookings.reduce((sum, booking) => sum + booking.amount, 0);

  const updateApplication = (id: string, status: AdminApplication["status"]) => {
    setState((current) => ({
      ...current,
      applications: current.applications.map((item) => (item.id === id ? { ...item, status } : item)),
    }));
  };

  const updateBooking = (id: string, status: AdminBooking["status"]) => {
    setState((current) => ({
      ...current,
      bookings: current.bookings.map((item) => (item.id === id ? { ...item, status } : item)),
    }));
  };

  const updateCase = (id: string, status: AdminCase["status"]) => {
    setState((current) => ({
      ...current,
      cases: current.cases.map((item) => (item.id === id ? { ...item, status } : item)),
    }));
  };

  return (
    <div className="admin-layout">
      <section className="dashboard-panel wide-panel">
        <SectionHeader
          icon={<LayoutDashboard size={22} />}
          title="Операционная панель"
          action="MVP alpha"
          text="Минимальные очереди для ручного управления маркетплейсом."
        />
        <div className="metric-grid">
          <Metric label="Заявки" value={applicationCounts.all} helper={`${applicationCounts.review} требуют действий`} />
          <Metric label="Записи сегодня" value={state.bookings.length} helper={`${formatRub(bookingRevenue)} ₽ оборот`} />
          <Metric label="Жалобы/замены" value={state.cases.length} helper="1 высокий приоритет" />
          <Metric label="Фискализация" value={state.fiscalizationMode} helper="платежи отключены" />
        </div>
      </section>

      <QueuePanel
        title="Заявки специалистов"
        icon={<UserRoundCheck size={22} />}
        rows={state.applications}
        render={(item) => (
          <QueueRow
            key={item.id}
            title={`${item.name} · ${item.role}`}
            meta={`${item.id} · подано ${item.submitted}`}
            status={item.status}
            actions={["Документы", "Интервью", "Одобрить"].map((status) => (
              <button
                key={status}
                className="mini-action"
                onClick={() => updateApplication(item.id, status as AdminApplication["status"])}
                type="button"
              >
                {status}
              </button>
            ))}
          />
        )}
      />

      <QueuePanel
        title="Бронирования"
        icon={<CalendarDays size={22} />}
        rows={state.bookings}
        render={(item) => (
          <QueueRow
            key={item.id}
            title={`${item.time} · ${item.pair}`}
            meta={`${item.id} · ${formatRub(item.amount)} ₽`}
            status={item.status}
            actions={["Ожидает", "Подтверждено", "Отмена"].map((status) => (
              <button
                key={status}
                className="mini-action"
                onClick={() => updateBooking(item.id, status as AdminBooking["status"])}
                type="button"
              >
                {status}
              </button>
            ))}
          />
        )}
      />

      <QueuePanel
        title="Жалобы и замены"
        icon={<ShieldCheck size={22} />}
        rows={state.cases}
        render={(item) => (
          <QueueRow
            key={item.id}
            title={`${item.id} · ${item.title}`}
            meta={`${item.participants} · приоритет ${item.priority}`}
            status={item.status}
            actions={["Новый", "В работе", "Решено"].map((status) => (
              <button
                key={status}
                className="mini-action"
                onClick={() => updateCase(item.id, status as AdminCase["status"])}
                type="button"
              >
                {status}
              </button>
            ))}
          />
        )}
      />

      <section className="dashboard-panel">
        <SectionHeader
          icon={<CreditCard size={22} />}
          title="Платежи и фискализация"
          action="Заглушка"
          text="Пока только модель. Реальные платежи подключаются после юрмодели."
        />
        <div className="form-grid">
          <Field label="Режим">
            <select
              value={state.fiscalizationMode}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  fiscalizationMode: event.target.value as AdminState["fiscalizationMode"],
                }))
              }
            >
              <option>Не подключено</option>
              <option>Песочница</option>
              <option>Готово</option>
            </select>
          </Field>
          <div className="prototype-box compact-box">
            <Settings size={22} />
            <div>
              <strong>Интеграция не активна</strong>
              <span>Провайдер, чеки и выплаты будут отдельным слоем после решений по юрмодели.</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({
  icon,
  title,
  text,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  action?: string;
}) {
  return (
    <div className="section-header">
      <span className="section-icon">{icon}</span>
      <div>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
      {action && <span className="status-badge">{action}</span>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <Field label={label}>
      <input
        inputMode="numeric"
        min="0"
        type="number"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </Field>
  );
}

function ScheduleBoard() {
  return (
    <div className="schedule-board" aria-label="Расписание специалиста">
      <div className="schedule-head" />
      {scheduleDays.map((day) => (
        <div className="schedule-head" key={day}>
          {day}
        </div>
      ))}
      {demoSchedule.map((row, rowIndex) => (
        <div className="schedule-row-group" key={rowIndex}>
          <div className="schedule-time">{["10:00", "12:00", "14:00", "18:00", "20:00"][rowIndex]}</div>
          {row.map((slot, colIndex) => (
            <button
              key={`${rowIndex}-${colIndex}`}
              className={slot ? "schedule-slot available" : "schedule-slot"}
              type="button"
            >
              {slot}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function Metric({ label, value, helper }: { label: string; value: string | number; helper: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{helper}</p>
    </div>
  );
}

function QueuePanel<T>({
  title,
  icon,
  rows,
  render,
}: {
  title: string;
  icon: React.ReactNode;
  rows: T[];
  render: (row: T) => React.ReactNode;
}) {
  return (
    <section className="dashboard-panel queue-panel">
      <SectionHeader icon={icon} title={title} action={`${rows.length}`} text="Статусы можно менять прямо в демо." />
      <div className="queue-list">{rows.map(render)}</div>
    </section>
  );
}

function QueueRow({
  title,
  meta,
  status,
  actions,
}: {
  title: string;
  meta: string;
  status: string;
  actions: React.ReactNode[];
}) {
  return (
    <article className="queue-row">
      <div>
        <strong>{title}</strong>
        <span>{meta}</span>
      </div>
      <span className={`queue-status ${statusClass(status)}`}>{status}</span>
      <div className="queue-actions">{actions}</div>
    </article>
  );
}

function statusClass(status: string) {
  if (
    [
      "Одобрен",
      "Одобрить",
      "Подтверждено",
      "Решено",
      "Готово",
      "Каталог",
      "Анкета сохранена",
      "Контакт подтверждён",
      "Передана в подбор",
      "На модерации",
    ].includes(status)
  ) {
    return "good";
  }
  if (
    [
      "В работе",
      "Интервью",
      "Документы",
      "Ожидает",
      "Песочница",
      "Бронирование",
      "Ожидает запись",
    ].includes(status)
  ) {
    return "warn";
  }
  if (
    ["Новый", "Новая", "Новая заявка", "Отмена", "Не подключено", "Заглушка", "Позже", "Поддержка"].includes(
      status,
    )
  ) {
    return "danger";
  }
  return "";
}

function QuestionBlock({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="question-block">
      <div>
        <h2>{title}</h2>
        {hint && <p>{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function TherapistCard({
  therapist,
  active,
  best,
  onSelect,
}: {
  therapist: Therapist;
  active: boolean;
  best: boolean;
  onSelect: () => void;
}) {
  return (
    <article className={active ? "therapist-card active" : "therapist-card"} onClick={onSelect}>
      {best && <span className="best-label">Лучшее совпадение</span>}
      <div className="therapist-main">
        <Portrait index={therapist.portraitIndex} />
        <div className="therapist-copy">
          <div className="name-line">
            <h3>{therapist.name}</h3>
            <span className="rating">
              <Star size={15} fill="currentColor" />
              {therapist.rating} ({therapist.reviews})
            </span>
          </div>
          <p>{therapist.title}</p>
          <span>Психолог, уровень {levelLabel[therapist.level]}</span>
          <div className="fact-row">
            <span>
              <UserRoundCheck size={16} />
              Опыт {therapist.experience} лет
            </span>
            <span>
              <ShieldCheck size={16} />
              Подтверждено
            </span>
          </div>
        </div>
        <div className="price-col">
          <strong>{formatRub(therapist.price)} ₽</strong>
          <span>/ сессия</span>
          <button className={active ? "radio active" : "radio"} aria-label={`Выбрать ${therapist.name}`}>
            {active && <Check size={15} />}
          </button>
        </div>
      </div>
      <div className="fit-box">
        <div>
          <b>Почему подходит вам</b>
          <p>{therapist.reason}</p>
        </div>
        <div className="proof-list">
          <span>
            <ShieldCheck size={18} />
            {therapist.evidence}
          </span>
          <span>
            <LockKeyhole size={18} />
            Безопасное пространство
          </span>
        </div>
      </div>
    </article>
  );
}

function Portrait({ index }: { index: number }) {
  const positions = [
    "0% 0%",
    "100% 0%",
    "0% 50%",
    "100% 50%",
    "0% 100%",
    "100% 100%",
  ];

  return (
    <span
      className="portrait"
      style={{
        backgroundImage: `url(${portraitSheet})`,
        backgroundPosition: positions[index],
      }}
      aria-hidden="true"
    >
      <span />
    </span>
  );
}

function SafetyItem({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <article className="safety-item">
      <span className="safety-icon">{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </article>
  );
}

export default App;
